import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import { isNested, insideText } from "../features/salesOrders/palletRules";
import config from "../config";
import { useOutlinePaths, OutlineOverlay, PartOutlineImage } from "../utils/packagingOutline.jsx";
import { spotProgress, stackUnits, unitWord, zoneStackLayout } from "../utils/stackLayout";
import { SurplusCardBody, SurplusCratesContext, SurplusRackStrip, SurplusRefreshContext, SurplusWhere } from "../components/SurplusRack";
import { OwedCardBody } from "../components/OwedCard";
import SheetMapPanel from "../components/laserKiosk/SheetMapPanel";
import SignalLightBar from "../components/laserKiosk/SignalLightBar";
import { AllPartsUnload, BeaconChip, DoNotAddBanner, FillOrderChip } from "../components/laserKiosk/KioskUnload";
import KioskScanner, { PalletScanChip, PalletScanStrip } from "../components/laserKiosk/KioskScanner";
import { KioskScanContext } from "../features/laserKiosk/kioskScan";
import {
  ALL_PARTS, assemblyNote, defaultPartSelection, destinationsInFillOrder, doNotAddReason, holdingDestinationLabel,
  isHoldingDestination, shownFillOrder,
} from "../features/laserKiosk/kioskRules";

// Local copy of the jobq-cam-kiosk HlsVideo config (which is proven to
// play 8 simultaneous tiles on a Pi 4 + cage Wayland setup). Key
// differences from this repo's shared <HlsVideo>:
//   - no `lowLatencyMode: true` override (default false)
//   - no liveSyncDurationCount / capLevelToPlayerSize wrinkles
// Plus an xhrSetup hook to inject the CF Access service-token headers
// because this UI calls cameras.kancraftmfg.com directly (cam-kiosk
// proxies through its localhost backend).
function KioskHlsPlayer({ src, authHeaders, style }) {
  const videoRef = useRef(null);
  const headersStr = useMemo(
    () => (authHeaders ? JSON.stringify(authHeaders) : ""),
    [authHeaders]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const headers = headersStr ? JSON.parse(headersStr) : null;
    let hls = null;
    let recoverTimer = null;

    const attach = () => {
      if (Hls.isSupported()) {
        hls = new Hls({
          maxBufferLength: 4,
          maxMaxBufferLength: 8,
          liveSyncDuration: 1.0,
          liveMaxLatencyDuration: 4.0,
          backBufferLength: 0,
          xhrSetup: (xhr) => {
            if (headers) {
              for (const [k, v] of Object.entries(headers)) {
                xhr.setRequestHeader(k, v);
              }
            }
          },
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          if (recoverTimer != null) return;
          recoverTimer = window.setTimeout(() => {
            recoverTimer = null;
            if (hls) { hls.destroy(); hls = null; }
            attach();
          }, 1500);
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
      }
    };
    attach();

    return () => {
      if (recoverTimer != null) {
        window.clearTimeout(recoverTimer);
        recoverTimer = null;
      }
      if (hls) { hls.destroy(); hls = null; }
    };
  }, [src, headersStr]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      style={style}
    />
  );
}

// ─── Camera relay config (matches CamerasPage.jsx) ────────────────────
// The laser kiosk pulls a live tile of the laser cutter so the operator
// can glance up and see the cut. Camera "1" maps to index 0
// (CamerasPage displays `Cam ${index + 1}`); change LASER_CAM_INDEX
// to swap which physical camera the kiosk shows.
const CAMERA_BASE_URL = "https://cameras.kancraftmfg.com";
const CF_ACCESS_HEADERS = {
  "CF-Access-Client-Id": "f6aa5936667f527290786c8d7b896774.access",
  "CF-Access-Client-Secret": "53e231d91de00cb864cfe49535c6914434a4712bfd3fccfa4a20e3062d5799d8",
};
const LASER_CAM_INDEX = 0; // operator-facing "Camera 1"
const LASER_CAM_SLUG = `laser-${LASER_CAM_INDEX}`;
// `&mp4` = go2rtc's fMP4 HLS (init.mp4 + .m4s). The plain TS variant starts
// every session on a segment with no PAT/PMT, so hls.js fails to demux it,
// reconnects into a new session that starts the same way, and the tile
// stays black or freezes (measured 2026-10-27: TS played 0 s of 40, fMP4 38).
const LASER_CAM_HLS_URL = `${CAMERA_BASE_URL}/go2rtc/stream.m3u8?src=${LASER_CAM_SLUG}&mp4`;

/**
 * LaserCurrentPackagingPage
 *
 * Dedicated Pi kiosk display at the laser workstation. Polls
 * GET /laser/current-packaging every 5s and renders the same
 * per-destination routing info the printed routing slips show:
 *   - per-destination QUANTITY
 *   - pallet + box identity (num, short_id, SKU, dims, position)
 *   - status badges (NEW BOX / IN-PROGRESS BOX / NEW PALLET / SURPLUS / Allocated)
 *   - "Needs N more pcs — expected from Job XXXX" for partials
 *   - routing destination (Route to: SO-NNNN · Customer)
 *
 * Multi-part jobs get a big-touch-target tab switcher at the top so
 * the operator can flip between parts while the laser runs.
 *
 * Design priorities over a normal UI page:
 *   - readable from across the shop (big fonts, high contrast)
 *   - no edit controls, no PIN prompts, no navigation
 *   - keep last-known plan on screen across transient fetch errors
 *   - chromeless — registered as a sibling of /station-feed (see App.jsx)
 */

function useKioskBody() {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.background = "#0f1115";
    return () => {
      document.body.style.overflow = "";
      document.body.style.background = "";
    };
  }, []);
}

const fmtClock = (d) =>
  d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });

// Parse "HH:MM:SS", "HH:MM", or PostgreSQL-style "N day(s), HH:MM:SS"
// interval string into total seconds. Backend serializes Python
// timedeltas with `str(td)`, which adds a "N day(s)," prefix when the
// interval exceeds 24h — without this branch the kiosk countdown
// silently dropped the days.
function parseTimeStr(timeStr) {
  if (!timeStr) return 0;
  const s = String(timeStr).trim();
  let total = 0;
  let rest = s;
  const dayMatch = s.match(/^(\d+)\s*days?,?\s*/i);
  if (dayMatch) {
    total = parseInt(dayMatch[1], 10) * 86400;
    rest = s.slice(dayMatch[0].length);
  }
  const parts = rest.split(":");
  if (parts.length === 3) {
    total += (parseInt(parts[0], 10) || 0) * 3600
           + (parseInt(parts[1], 10) || 0) * 60
           + (parseFloat(parts[2]) || 0);
  } else if (parts.length === 2) {
    total += (parseInt(parts[0], 10) || 0) * 3600
           + (parseInt(parts[1], 10) || 0) * 60;
  }
  return total;
}

function fmtHMS(secs, { signed = false } = {}) {
  const sign = secs < 0 ? "-" : (signed ? "+" : "");
  const a = Math.abs(Math.floor(secs));
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const s = a % 60;
  return `${sign}${h}h ${m.toString().padStart(2, "0")}m ${s.toString().padStart(2, "0")}s`;
}

// Live-updating elapsed/remaining widget. Mirrors mobile CountdownTimer:
// historicElapsedSeconds is `task.elapsed_time_seconds` (DB total before
// the latest start), startTimeStr is `task.last_start_time`.
function LiveTimeWidget({
  estimatedStr,
  elapsedSeconds,
  startTimeStr,
  status,
}) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (status !== "In Progress") return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const estSec = parseTimeStr(estimatedStr);
  let liveElapsed = elapsedSeconds || 0;
  if (status === "In Progress" && startTimeStr) {
    const start = new Date(startTimeStr).getTime();
    if (!Number.isNaN(start)) {
      liveElapsed += Math.max(0, (Date.now() - start) / 1000);
    }
  }
  const remain = estSec - liveElapsed;
  const overdue = remain < 0;
  const blockColor = status === "In Progress"
    ? (overdue ? "#5a1d1d" : "#0e2a3e")
    : (status === "Paused" ? "#3a2e0c" : "#23262e");
  const titleColor = status === "In Progress"
    ? (overdue ? "#ff8888" : "#7ad7ff")
    : (status === "Paused" ? "#ffd76b" : "#9aa4b4");

  return (
    <div style={{
      background: blockColor, padding: "0.55rem 1rem", borderRadius: 10,
      display: "flex", flexDirection: "column", gap: 2, minWidth: 240,
    }}>
      <div style={{ fontSize: "0.85rem", color: titleColor, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}>
        {overdue ? "Time Over" : "Time Left"}
      </div>
      <div style={{
        fontSize: "1.95rem", fontWeight: 700, fontFamily: "monospace",
        color: overdue ? "#ff8888" : (status === "In Progress" ? "#fff" : "#d8e3ee"),
        lineHeight: 1.05,
      }}>
        {fmtHMS(Math.abs(remain))}
      </div>
      <div style={{ fontSize: "0.8rem", color: "#9aa4b4", fontFamily: "monospace" }}>
        Elapsed {fmtHMS(liveElapsed)} <span style={{ color: "#5e6976" }}>·</span> Est {estSec > 0 ? fmtHMS(estSec) : "—"}
      </div>
    </div>
  );
}

// Cut-sheet + material info card. Surfaces cut sheet name, material
// description + dims, owner (when customer-supplied), and a per-line
// material trace summary (heat #, PO, supplier, shipment) — matches the
// info ops/operators see on the desktop laser job page.
// Time + sheets + cut-sheet info for the FOCUSED job (the one whose
// packaging guide is being viewed below). Stays "live" — when the
// focused job is the current Laser task, LiveTimeWidget ticks down;
// when viewing a Previous job, the timer freezes at completion state.
function CurrentJobInfoCard({ task, isLive, isViewing, onClickToReturn }) {
  if (!task) return null;
  const sheetReq = task.sheet_required ?? null;
  // Laser job-level tasks (part_id=NULL + cut_sheet_id) keep qty_completed
  // at 0 — the running sheet count lives on jt.sheet_count instead.
  const sheetCut = task.sheet_count ?? task.qty_completed ?? 0;
  const sheetsComplete = sheetReq != null && sheetCut >= sheetReq && sheetReq > 0;
  const matLine = [
    task.material_description,
    task.material_length_in && task.material_width_in
      ? `${Number(task.material_length_in)}″ × ${Number(task.material_width_in)}″`
      : null,
    task.material_thickness_in
      ? `${Number(task.material_thickness_in)}″ thick`
      : null,
  ].filter(Boolean).join(" · ");
  const trace = task.material_trace || {};
  const traceBits = [
    trace.heat_number && `Heat ${trace.heat_number}`,
    trace.po_number && `PO ${trace.po_number}`,
    trace.shipment_id && `Ship ${trace.shipment_id}`,
    trace.supplier && `← ${trace.supplier}`,
  ].filter(Boolean);
  const hasCert = traceBits.length > 0;

  // When viewing the LIVE current task, highlight subtly. When viewing
  // a non-current task (and the live one is clickable to return),
  // the wrapper is clickable.
  const wrapperStyle = {
    display: "flex",
    gap: "0.7rem",
    flex: 1,
    minWidth: 0,
    alignItems: "stretch",
    padding: "0.25rem",
    borderRadius: 12,
    border: isViewing
      ? "2px solid #4ec9ff"
      : "2px solid transparent",
    cursor: onClickToReturn ? "pointer" : "default",
  };

  return (
    <div style={wrapperStyle} onClick={onClickToReturn || undefined}
         title={onClickToReturn ? "Return to the current Laser job" : undefined}>
      <LiveTimeWidget
        estimatedStr={task.estimated_time}
        elapsedSeconds={task.elapsed_time_seconds}
        startTimeStr={task.last_start_time}
        status={isLive ? task.task_status : "Completed"}
      />

      {sheetReq != null && (
        <div style={{
          background: sheetsComplete ? "#143a1f" : "#2a2f3a",
          padding: "0.55rem 1rem", borderRadius: 10,
          display: "flex", flexDirection: "column", gap: 2, minWidth: 130,
        }}>
          <div style={{
            fontSize: "0.85rem",
            color: sheetsComplete ? "#7ede9e" : "#9aa4b4",
            textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700,
          }}>
            Sheets {sheetsComplete && "✓"}
          </div>
          <div style={{
            fontSize: "1.95rem", fontWeight: 700, fontFamily: "monospace",
            color: sheetsComplete ? "#7ede9e" : "white", lineHeight: 1.05,
          }}>
            {sheetCut} <span style={{ color: "#9aa4b4", fontWeight: 400 }}>/ {sheetReq}</span>
          </div>
          <div style={{ fontSize: "0.8rem", color: "#9aa4b4" }}>
            {sheetsComplete ? "all loaded" : "load more"}
          </div>
        </div>
      )}

      {(task.cut_sheet_name || matLine) && (
        <div style={{
          background: "#1c1f26", padding: "0.55rem 1rem", borderRadius: 10,
          display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0,
        }}>
          {task.cut_sheet_name && (
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.8rem", color: "#9aa4b4", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Cut Sheet
              </span>
              <span style={{ fontSize: "1.1rem", fontFamily: "monospace", color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {task.cut_sheet_name}
              </span>
            </div>
          )}
          {matLine && (
            <div style={{ fontSize: "1.05rem", color: "#e8d28e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {matLine}
            </div>
          )}
          {task.material_owner_name && (
            <div style={{ fontSize: "0.95rem", color: "#bcd4ff" }}>
              Owner: <b>{task.material_owner_name}</b>
              <span style={{ marginLeft: 8, color: "#9aa4b4", fontSize: "0.8rem" }}>(customer-supplied)</span>
            </div>
          )}
          <div style={{ fontSize: "0.85rem", color: hasCert ? "#9aa4b4" : "#ffd76b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {hasCert ? traceBits.join(" · ") : "⚠ No material cert linked"}
          </div>
        </div>
      )}
    </div>
  );
}

// 3-card navigation strip: Previous Job | Current (centered) | Up Next.
// The Previous + Up Next cards are clickable — clicking them tells the
// kiosk to fetch + display that job's packaging guide. Common workflow:
// operator's still packing the previous job while a new one is running,
// or wants to pre-stage pallets for the next one mid-cut.
function JobInfoStrip({
  task,          // the focused task (what's being displayed below)
  liveTask,      // the LIVE current task (drives the prev/next neighbor cards)
  isViewingLive, // true when focused == live
  onViewPrevious,
  onViewNext,
  onViewLive,
}) {
  if (!task) return null;
  // Neighbor cards always reflect the LIVE current task's neighbors,
  // not the focused task's neighbors. Reason: the navigation is "what
  // was, what is, what's coming up" anchored on now — switching the
  // focus shouldn't switch the cards.
  const prev = liveTask?.previous_task || null;
  const next = liveTask?.next_task || null;
  const focusedJobId = task.job_id;
  const isViewingPrev = !!prev && prev.job_id === focusedJobId;
  const isViewingNext = !!next && next.job_id === focusedJobId;

  return (
    <div style={{
      display: "flex", gap: "0.7rem", flexShrink: 0, alignItems: "stretch",
    }}>
      <NeighborJobCard
        kind="previous"
        task={prev}
        onClick={prev && !isViewingPrev ? onViewPrevious : null}
        isViewing={isViewingPrev}
      />
      <CurrentJobInfoCard
        task={task}
        isLive={isViewingLive}
        isViewing={isViewingLive}
        onClickToReturn={!isViewingLive ? onViewLive : null}
      />
      <NeighborJobCard
        kind="next"
        task={next}
        onClick={next && !isViewingNext ? onViewNext : null}
        isViewing={isViewingNext}
      />
    </div>
  );
}

// Live camera tile, at the top of the kiosk's right-hand rail (KioskRail).
// In the layout flow since 2026-10-26: it used to be pinned over the
// bottom-right corner (position: fixed) and covered destination cards.
// Memoized so parent re-renders don't remount it (which would spawn
// duplicate go2rtc viewer sessions).
const LaserCameraPanel = React.memo(function LaserCameraPanel() {
  return (
    <div data-testid="laser-camera" style={{
      position: "relative",
      width: "100%",
      aspectRatio: "16 / 9",
      flexShrink: 0,
      background: "#000",
      border: "2px solid #2a2f3a",
      borderRadius: 12,
      overflow: "hidden",
    }}>
      <KioskHlsPlayer
        src={LASER_CAM_HLS_URL}
        authHeaders={CF_ACCESS_HEADERS}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
      <div style={{
        position: "absolute", top: 8, left: 10,
        background: "rgba(220, 53, 69, 0.85)",
        color: "white", fontSize: "0.8rem", fontWeight: 700,
        padding: "0.15rem 0.55rem", borderRadius: 5,
        letterSpacing: "0.05em",
      }}>
        ● LIVE
      </div>
      <div style={{
        position: "absolute", top: 8, right: 10,
        color: "rgba(255,255,255,0.9)", fontSize: "0.8rem", fontWeight: 600,
        background: "rgba(0,0,0,0.55)", padding: "0.15rem 0.55rem", borderRadius: 5,
      }}>
        LASER CAM {LASER_CAM_INDEX + 1}
      </div>
    </div>
  );
});

// The right-hand rail beside the part / destination views: the laser camera,
// then room for one more panel (pass it as children). Its own column, so
// nothing in it can ever cover a destination card.
function KioskRail({ children }) {
  return (
    <aside
      data-testid="kiosk-rail"
      style={{
        width: "clamp(260px, 22vw, 560px)",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "0.7rem",
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <LaserCameraPanel />
      {children}
    </aside>
  );
}

// "UP NEXT" card — surfaces the next pending Laser job's material so the
// operator can stage it before the current cut finishes. Pulls from the
// backend's next_task envelope, which mirrors /queue (JobQueuePage Laser)
// filtered to today's scheduled_date — same sort order, same Pending-only
// filter, only tasks with scheduled_date = today (Central Time).
// One of the three navigation cards (previous/current/next). Used for
// the Previous Job and Up Next cards. Clickable when there's a
// neighboring task AND a click handler — otherwise renders as an empty
// placeholder ("No recent jobs" / "No tasks scheduled for today").
//
// The "current" position is rendered by CurrentJobInfoCard instead
// (since it shows TIME LEFT + SHEETS + CUT SHEET, not just job
// metadata).
function NeighborJobCard({ kind, task, onClick, isViewing }) {
  // Theming: Previous = amber/warm, Up Next = green/cool, viewed = blue.
  const isPrev = kind === "previous";
  const label = isPrev ? "← PREVIOUS JOB" : "UP NEXT →";
  const baseBg = isPrev ? "#2a2218" : "#15201a";
  const baseBorder = isPrev ? "#5a4630" : "#2d7a3e";
  const labelColor = isPrev ? "#e8c878" : "#7ede9e";
  const viewingBorder = "#4ec9ff";

  const cardWidth = 280;
  const wrapperStyle = {
    background: baseBg,
    border: `2px solid ${isViewing ? viewingBorder : baseBorder}`,
    padding: "0.55rem 0.85rem",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: cardWidth,
    flexShrink: 0,
    cursor: onClick ? "pointer" : "default",
    opacity: task ? 1 : 0.7,
  };

  if (!task) {
    return (
      <div style={wrapperStyle}>
        <div style={{
          fontSize: "0.85rem", color: labelColor,
          textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700,
        }}>
          {label}
        </div>
        <div style={{ fontSize: "1.05rem", color: "#9aa4b4", fontStyle: "italic", marginTop: 4 }}>
          {isPrev ? "No recent Laser jobs" : "No tasks scheduled for today"}
        </div>
      </div>
    );
  }

  const matLine = [
    task.material_description,
    task.material_length_in && task.material_width_in
      ? `${Number(task.material_length_in)}″ × ${Number(task.material_width_in)}″`
      : null,
    task.material_thickness_in
      ? `${Number(task.material_thickness_in)}″ thick`
      : null,
  ].filter(Boolean).join(" · ");

  const titleHint = onClick
    ? `Click to view this job's packaging guide`
    : isViewing
      ? `Currently viewing this job`
      : null;

  return (
    <div style={wrapperStyle} onClick={onClick || undefined} title={titleHint || undefined}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
        <div style={{
          fontSize: "0.85rem", color: labelColor,
          textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700,
        }}>
          {label}
        </div>
        <div style={{ fontSize: "1.15rem", fontWeight: 700, fontFamily: "monospace", color: "white", marginLeft: "auto" }}>
          Job {task.master_job_num || `#${task.job_id}`}
        </div>
      </div>
      {task.order_number && (
        <div style={{ fontSize: "0.95rem", color: "#4ec9ff", fontFamily: "monospace" }}>
          {task.order_number}
        </div>
      )}
      {task.job_description && (
        <div style={{ fontSize: "0.9rem", color: "#bcd4ff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {task.job_description}
        </div>
      )}
      {matLine && (
        <div style={{ fontSize: "0.95rem", color: "#e8d28e", fontWeight: 600, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          📦 {matLine}
        </div>
      )}
      <div style={{ display: "flex", gap: "0.7rem", fontSize: "0.8rem", color: "#9aa4b4", flexWrap: "wrap" }}>
        {task.sheet_required > 0 && (
          <span>{task.sheet_required} sheet{task.sheet_required > 1 ? "s" : ""}</span>
        )}
        {task.estimated_time && <span>≈ {fmtHMS(parseTimeStr(task.estimated_time))}</span>}
        {task.material_owner_name && (
          <span style={{ color: "#bcd4ff" }}>· {task.material_owner_name} (CSM)</span>
        )}
      </div>
      {isViewing && (
        <div style={{
          marginTop: 2, fontSize: "0.75rem", color: viewingBorder,
          fontWeight: 700, letterSpacing: "0.05em", textAlign: "right",
        }}>
          ● VIEWING
        </div>
      )}
    </div>
  );
}

// ─── SKU color palette (matches SalesOrderPage conventions) ───────────
const BOX_SKU_COLOR = {
  "S-4827": "#4e79a7",
  "S-4831": "#f28e2b",
  "S-4841": "#e15759",
  "S-4563": "#76b7b2",
  "S-18246": "#59a14f",
};
const fillForBox = (bx) => {
  if (bx.is_pallet_direct) return "url(#palletDirectHatch)";
  return BOX_SKU_COLOR[bx.sku] || "#8da0b4";
};

// Compute a px/inch scale for the enlarge modal so the SVG fills the
// available viewport area (~85vw / 78vh) instead of rendering at a
// fixed scale that leaves big margins on a wide monitor. Mirrors the
// internal layout maths in PalletMapSVG / BoxInteriorSVG.
function computePalletEnlargedScale(plan) {
  if (!plan || !plan.pallets || !plan.pallets.length) return 14;
  const pallets = plan.pallets.filter((p) => !p.consolidated_into_pallet_id);
  if (!pallets.length) return 14;
  const perRow = pallets.length <= 2 ? pallets.length : pallets.length <= 4 ? 2 : 3;
  const maxL = Math.max(...pallets.map((p) => p.length_in || 40));
  const maxW = Math.max(...pallets.map((p) => p.width_in || 48));
  const totalW = maxL * perRow + 6 * (perRow - 1);
  const rows = Math.ceil(pallets.length / perRow);
  const labelHeight = 14;
  const totalH = (maxW + labelHeight) * rows + 6 * (rows - 1);
  const targetW = window.innerWidth * 0.85;
  const targetH = window.innerHeight * 0.78;
  return Math.max(6, Math.floor(Math.min(targetW / totalW, targetH / totalH)));
}

function computeBoxEnlargedScale(box) {
  const inL = (box && (box.pallet_rotated ? box.outer_w : box.outer_l)) || 24;
  const inW = (box && (box.pallet_rotated ? box.outer_l : box.outer_w)) || 18;
  const targetW = window.innerWidth * 0.85;
  const targetH = window.innerHeight * 0.78;
  return Math.max(8, Math.floor(Math.min(targetW / inL, targetH / inW)));
}

// ─── Pallet map SVG (shared by thumbnail + enlarge modal) ─────────────
function PalletMapSVG({
  plan,
  highlightPalletId,
  highlightBoxId,
  outlineCache,
  scalePxPerIn = 1.5,
  showLabels = false,
}) {
  if (!plan || !plan.pallets) return null;
  const pallets = plan.pallets.filter((p) => !p.consolidated_into_pallet_id);
  if (pallets.length === 0) return null;
  // Lay pallets out in a 2- or 3-wide grid so the thumb stays roughly square.
  const perRow = pallets.length <= 2 ? pallets.length : pallets.length <= 4 ? 2 : 3;
  const gap = 6;
  const rows = [];
  for (let i = 0; i < pallets.length; i += perRow) rows.push(pallets.slice(i, i + perRow));
  // Compute svg dimensions.
  const rowWidths = rows.map((r) =>
    r.reduce((w, p) => w + (p.length_in || 40) * scalePxPerIn, 0) + (r.length - 1) * gap);
  const rowHeights = rows.map((r) =>
    Math.max(...r.map((p) => (p.width_in || 48) * scalePxPerIn)));
  const svgW = Math.max(...rowWidths);
  const svgH = rowHeights.reduce((a, h) => a + h, 0) + (rows.length - 1) * gap + (showLabels ? 14 * rows.length : 0);

  let yCursor = 0;
  const palletNodes = [];
  rows.forEach((row, ri) => {
    let xCursor = 0;
    row.forEach((p) => {
      const w = (p.length_in || 40) * scalePxPerIn;
      const h = (p.width_in || 48) * scalePxPerIn;
      const isHi = p.pallet_id === highlightPalletId;
      palletNodes.push(
        <g key={p.pallet_id} transform={`translate(${xCursor},${yCursor + (showLabels ? 12 : 0)})`}>
          {showLabels && (
            <text x={0} y={-2} fontSize={11} fill="#e0e4ea" fontFamily="monospace" fontWeight={700}>
              {p.short_id || `P${p.pallet_num}`}
            </text>
          )}
          <rect x={0} y={0} width={w} height={h}
            fill="#fafafa" stroke={isHi ? "#ff6a00" : "#555"} strokeWidth={isHi ? 2.4 : 1} />
          {(p.boxes || []).filter((bx) => !isNested(bx)).map((bx) => {
            const bol = bx.pallet_rotated ? bx.outer_w : bx.outer_l;
            const bow = bx.pallet_rotated ? bx.outer_l : bx.outer_w;
            const bx0 = (bx.pallet_pos_x || 0) * scalePxPerIn;
            const by0 = (bx.pallet_pos_y || 0) * scalePxPerIn;
            const bw = (bol || 0) * scalePxPerIn;
            const bh = (bow || 0) * scalePxPerIn;
            // A nested box (inside another order's carton) is not drawn: its carton lights up instead.
            const isBoxHi = bx.box_id === highlightBoxId
              || (p.boxes || []).some((nb) => nb.box_id === highlightBoxId && nb.inside_box_id === bx.box_id);
            const rot = !!bx.pallet_rotated;
            const boxOuterW = bx.outer_w || 0;
            return (
              <g key={bx.box_id}>
                <rect
                  x={bx0} y={by0} width={bw} height={bh}
                  fill={fillForBox(bx)}
                  fillOpacity={isBoxHi ? 1 : 0.55}
                  stroke={isBoxHi ? "#ff6a00" : "#333"}
                  strokeWidth={isBoxHi ? 2.5 : 0.6}
                />
                {/* Per-zone DXF outline overlay — true-to-scale rendering
                    matching the SO Packaging Plan card. Tile grids on
                    pallet-direct stacks get one outline per cell so the
                    operator sees the actual shape filling each column. */}
                {(bx.zones || []).map((z, zi) => {
                  const data = (outlineCache && z.outline_svg_path)
                    ? outlineCache[z.outline_svg_path] : null;
                  if (!data || !data.paths || data.paths.length === 0) return null;
                  // Zone position in pallet space — same rotation logic
                  // as the SO PalletMap.
                  const localPosA = z.zone_x || 0;
                  const localPosB = boxOuterW - (z.zone_y || 0) - (z.zone_h || 0);
                  const palOffX = (rot ? localPosB : localPosA) * scalePxPerIn;
                  const palOffY = (rot ? localPosA : localPosB) * scalePxPerIn;
                  const zw = (rot ? (z.zone_h || 0) : (z.zone_w || 0)) * scalePxPerIn;
                  const zh = (rot ? (z.zone_w || 0) : (z.zone_h || 0)) * scalePxPerIn;
                  const zx = bx0 + palOffX;
                  const zy = by0 + palOffY;
                  const tcSvg = rot ? (z.tile_rows || 0) : (z.tile_cols || 0);
                  const trSvg = rot ? (z.tile_cols || 0) : (z.tile_rows || 0);
                  // Tile-grid pallet-direct: one outline per cell.
                  if (tcSvg > 0 && trSvg > 0) {
                    const cells = [];
                    const cw = zw / tcSvg;
                    const ch = zh / trSvg;
                    if (Math.min(cw, ch) < 5) return null;
                    for (let r = 0; r < trSvg; r++) {
                      for (let c = 0; c < tcSvg; c++) {
                        cells.push(
                          <OutlineOverlay key={`${z.zone_id || zi}-${r}-${c}`}
                            data={data}
                            cx={zx + c * cw + 0.5} cy={zy + r * ch + 0.5}
                            cw={cw - 1} ch={ch - 1}
                            autoOrient
                            opacity={isBoxHi ? 0.95 : 0.7} />
                        );
                      }
                    }
                    return <g key={z.zone_id || zi}>{cells}</g>;
                  }
                  // Boxed zone: one outline filling the zone footprint.
                  if (Math.min(zw, zh) < 6) return null;
                  return (
                    <OutlineOverlay key={z.zone_id || zi}
                      data={data}
                      cx={zx + 0.5} cy={zy + 0.5}
                      cw={zw - 1} ch={zh - 1}
                      autoOrient
                      opacity={isBoxHi ? 0.95 : 0.7} />
                  );
                })}
                {showLabels && bw > 14 && bh > 10 && (
                  <text
                    x={bx0 + bw / 2} y={by0 + bh / 2 + 3}
                    fontSize={Math.min(11, Math.max(6, Math.min(bw, bh) / 3))}
                    textAnchor="middle" fill="#111" fontFamily="monospace" fontWeight={700}
                    style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 2 }}>
                    {bx.short_id
                      ? bx.short_id.replace(/^[A-Z]+-/, '')
                      : (bx.is_pallet_direct ? `S${bx.box_num}` : `B${bx.box_num}`)}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      );
      xCursor += w + gap;
    });
    yCursor += rowHeights[ri] + gap + (showLabels ? 14 : 0);
  });

  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} style={{ background: "#222831", borderRadius: 6, maxWidth: "100%", height: "auto", display: "block" }}>
      <defs>
        <pattern id="palletDirectHatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#6c757d" strokeWidth="1.2" />
        </pattern>
      </defs>
      {palletNodes}
    </svg>
  );
}

// ─── Box interior SVG — zones + tile grid ─────────────────────────────
function BoxInteriorSVG({
  box,
  highlightPartId,
  outlineCache,
  scalePxPerIn = 4,
  showLabels = false,
}) {
  if (!box) return null;
  const inL = (box.pallet_rotated ? box.outer_w : box.outer_l) || 24;
  const inW = (box.pallet_rotated ? box.outer_l : box.outer_w) || 18;
  const svgW = inL * scalePxPerIn;
  const svgH = inW * scalePxPerIn;
  const zones = box.zones || [];
  // When zones lack geometry, fall back to equal horizontal strips.
  const anyGeo = zones.some((z) => z.zone_w > 0 && z.zone_h > 0);
  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} style={{ background: "#fafafa", borderRadius: 6, border: "1px solid #333", maxWidth: "100%", height: "auto", display: "block", boxSizing: "border-box" }}>
      <rect x={0} y={0} width={svgW} height={svgH} fill="#fff" stroke="#333" strokeWidth={1} />
      {zones.map((z, idx) => {
        let zx, zy, zw, zh;
        if (anyGeo) {
          zx = (z.zone_x || 0) * scalePxPerIn;
          zy = (z.zone_y || 0) * scalePxPerIn;
          zw = (z.zone_w || 0) * scalePxPerIn;
          zh = (z.zone_h || 0) * scalePxPerIn;
        } else {
          zx = 0;
          zh = svgH / Math.max(zones.length, 1);
          zy = idx * zh;
          zw = svgW;
        }
        const isHi = z.part_id === highlightPartId;
        const tcols = z.tile_cols || 0;
        const trows = z.tile_rows || 0;
        const planned = z.planned_qty || 0;
        const actual = z.actual_qty || 0;
        const fillPct = planned > 0 ? actual / planned : 0;
        const zoneFill = isHi
          ? (fillPct >= 1 ? "#28a745" : fillPct > 0 ? "#8fd19e" : "#cfe9d6")
          : (fillPct >= 1 ? "#c0d7c5" : "#eceef0");
        // Pre-rendered DXF outline (same data path the SO Packaging Plan
        // card uses). Drawn at the part's true bbox scale, so multiple
        // zones holding the same part render the part at identical
        // physical size — zone size only changes the surrounding
        // colored area, not the outline.
        const outlineData = (outlineCache && z.outline_svg_path)
          ? outlineCache[z.outline_svg_path] : null;
        const hasOutline = !!(outlineData && outlineData.paths && outlineData.paths.length);
        // Each spot's own target height (the routing slip / packing plan rule:
        // the first spots carry the partial top layer's extra piece) and how
        // much of it is on the pallet, filling spot by spot.
        const spots = (tcols > 0 && trows > 0)
          ? spotProgress(zoneStackLayout(z), stackUnits(actual, z.assembly_id, z.primary_part_quantity))
          : [];
        return (
          <g key={z.zone_id || idx}>
            {tcols > 0 && trows > 0 ? (
              // Tile grid — draw one rect per tile, filled by its individual status.
              Array.from({ length: tcols * trows }).map((_, i) => {
                const cx = i % tcols, cy = Math.floor(i / tcols);
                const tw = zw / tcols;
                const th = zh / trows;
                const spot = spots[i] || { target: 0, got: 0 };
                const isTileFull = spot.target > 0 && spot.got >= spot.target;
                const isTilePartial = spot.got > 0 && spot.got < spot.target;
                const tileFill = isHi
                  ? (isTileFull ? "#28a745" : isTilePartial ? "#8fd19e" : "#cfe9d6")
                  : (fillPct >= 1 ? "#c0d7c5" : "#eceef0");
                return (
                  <g key={i}>
                    <rect
                      x={zx + cx * tw + 0.5}
                      y={zy + cy * th + 0.5}
                      width={tw - 1}
                      height={th - 1}
                      fill={tileFill}
                      stroke={isHi ? "#145421" : "#888"}
                      strokeWidth={isHi ? 1 : 0.4}
                    />
                    {hasOutline && Math.min(tw, th) >= 8 && (
                      <OutlineOverlay data={outlineData}
                        cx={zx + cx * tw + 1} cy={zy + cy * th + 1}
                        cw={tw - 2} ch={th - 2}
                        autoOrient
                        opacity={0.85} />
                    )}
                    {showLabels && tw > 30 && th > 14 && (
                      <text
                        x={zx + cx * tw + tw / 2}
                        y={zy + cy * th + th / 2 + 3}
                        fontSize={Math.min(10, tw / 4)}
                        textAnchor="middle" fill="#1a1a1a" fontFamily="monospace"
                        style={hasOutline ? { paintOrder: "stroke", stroke: "#fff", strokeWidth: 2 } : null}>
                        {spot.target ? `${spot.got}/${spot.target}` : "–"}
                      </text>
                    )}
                  </g>
                );
              })
            ) : (
              // No tile grid — single-zone fill
              <>
                <rect x={zx} y={zy} width={zw} height={zh}
                  fill={zoneFill}
                  stroke={isHi ? "#145421" : "#888"}
                  strokeWidth={isHi ? 2 : 0.6}
                />
                {hasOutline && Math.min(zw, zh) >= 10 && (
                  <OutlineOverlay data={outlineData}
                    cx={zx + 1} cy={zy + 1}
                    cw={zw - 2} ch={zh - 2}
                    autoOrient
                    opacity={0.85} />
                )}
                {showLabels && zw > 30 && zh > 14 && (
                  <text
                    x={zx + zw / 2} y={zy + zh / 2 + 4}
                    fontSize={Math.min(12, zw / 6)}
                    textAnchor="middle" fill="#1a1a1a" fontFamily="monospace" fontWeight={700}
                    style={hasOutline ? { paintOrder: "stroke", stroke: "#fff", strokeWidth: 2 } : null}>
                    {actual}/{planned}
                  </text>
                )}
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Click-to-enlarge modal ───────────────────────────────────────────
function EnlargeModal({ title, onClose, children }) {
  // Dismiss on ESC or any background click.
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999,
        padding: "2rem",
        cursor: "pointer",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1a1d24",
          borderRadius: 16,
          padding: "1.5rem 2rem",
          maxWidth: "95vw",
          maxHeight: "95vh",
          overflow: "auto",
          cursor: "default",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
          <div style={{ fontSize: "1.8rem", color: "white", fontWeight: 700, flex: 1 }}>
            {title}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#e15759", color: "white", border: "none",
              padding: "0.5rem 1.2rem", borderRadius: 8,
              fontSize: "1.3rem", fontWeight: 700, cursor: "pointer",
            }}
          >
            Close ✕
          </button>
        </div>
        <div style={{ color: "#e0e4ea" }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Badge primitives ─────────────────────────────────────────────────
function Badge({ children, bg, color = "white" }) {
  return (
    <span
      style={{
        background: bg,
        color,
        padding: "0.25rem 0.7rem",
        borderRadius: 6,
        fontWeight: 700,
        fontSize: "1rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function PalletBadge({ dest }) {
  // Live pallet status from packing_plans takes precedence — placement
  // snapshots from before the pickup workflow shipped (Apr 26 2026) had
  // no concept of Staged. The live-state enrichment refreshes
  // pallet_status from planned_pallets.
  const liveStatus = (dest.pallet_status || "").toLowerCase();
  if (liveStatus === "staged")   return <Badge bg="#0dcaf0">AWAITING PICKUP</Badge>;
  if (liveStatus === "shipped")  return <Badge bg="#198754">SHIPPED</Badge>;
  if (liveStatus === "ready")    return <Badge bg="#ffc107" color="#0f1115">READY</Badge>;
  if (dest.pallet_was_empty)     return <Badge bg="#28a745">NEW PALLET</Badge>;
  return <Badge bg="#d39e00">IN-PROGRESS</Badge>;
}

function BoxBadge({ dest }) {
  const prior = (dest.prior_box_status || "").toLowerCase();
  const noun = dest.is_pallet_direct ? "STACK" : "BOX";
  if (prior === "empty" || dest.status_transition === "Empty→Partial" || dest.status_transition === "Empty→Full")
    return <Badge bg="#28a745">{`NEW ${noun}`}</Badge>;
  return <Badge bg="#d39e00">{`IN-PROGRESS ${noun}`}</Badge>;
}

function AllocatedBadge({ dest }) {
  if (dest.dest_tag === "Allocated") return <Badge bg="#0d6efd">ALLOCATED</Badge>;
  return null;
}

// Compact relative-time formatter for sealed-at / shipped-at chips.
// "5m ago", "2h ago", "1d ago". Returns null for missing / invalid.
function fmtRelTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const dSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (dSec < 60)        return `${dSec}s ago`;
  if (dSec < 3600)      return `${Math.floor(dSec / 60)}m ago`;
  if (dSec < 86400)     return `${Math.floor(dSec / 3600)}h ago`;
  return `${Math.floor(dSec / 86400)}d ago`;
}

// ─── Full-screen state banners ─────────────────────────────────────────
function BigBanner({ title, subtitle, tone = "info" }) {
  const bg = {
    info: "#1a3a52",
    warn: "#5a4a16",
    idle: "#222831",
  }[tone] || "#1a3a52";
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: bg,
        color: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "3rem",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: "6rem", fontWeight: 700, letterSpacing: "-2px" }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: "2.5rem", marginTop: "1.5rem", opacity: 0.9 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ─── One destination card (packaging, owed or surplus) ────────────────
function DestinationCard({ dest, part, plansBySo, outlineCache, onEnlarge, order }) {
  const blocked = doNotAddReason(dest);
  const orderChip = order ? (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.2rem" }}>
      <FillOrderChip order={order} />
      <span style={{ color: "#c9d4e2", fontSize: "1rem", fontWeight: 700 }}>
        {order.seq === 1 ? "fill first" : order.seq === order.of ? "fill last" : "then"}
      </span>
    </div>
  ) : null;
  if (dest.kind === "owed") {
    return (
      <div
        data-testid="owed-card"
        style={{
          background: "#3a1c1a",
          border: "3px solid #c0392b",
          borderRadius: 14,
          padding: "1.1rem 1.3rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.4rem",
          minHeight: 360,
        }}
      >
        {orderChip}
        <OwedCardBody dest={dest} />
      </div>
    );
  }
  const isSurplus = dest.kind === "surplus";
  const baseBg = blocked ? "#3a0a12" : isSurplus ? "#3a3320" : "#14321d";
  const border = blocked ? "#ff3b4e" : isSurplus ? "#a8882b" : "#2d7a3e";
  // Interior dims match the Uline catalog name printed on the carton
  // (e.g. S-4827 = "24×18×4") and the SO packaging guide. Outer dims
  // (24.75×18.75×4.75) round to 25×19×5 — confusing for operators
  // matching the kiosk to the box label.
  const dims = dest.box_interior_l && dest.box_interior_w && dest.box_interior_h
    ? `${(+dest.box_interior_l)}×${(+dest.box_interior_w)}×${(+dest.box_interior_h)}″`
    : (dest.box_outer_l && dest.box_outer_w && dest.box_outer_h
      ? `${Math.round(dest.box_outer_l)}×${Math.round(dest.box_outer_w)}×${Math.round(dest.box_outer_h)}″`
      : null);
  const posText = (dest.pallet_pos_x != null && dest.pallet_pos_y != null)
    ? `(${dest.pallet_pos_x.toFixed(1)}″, ${dest.pallet_pos_y.toFixed(1)}″)`
    : null;

  return (
    <div
      style={{
        background: baseBg,
        border: `${blocked ? 5 : 3}px solid ${border}`,
        borderRadius: 14,
        padding: "1.1rem 1.3rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        minHeight: 360,
      }}
    >
      {orderChip}
      {blocked && <DoNotAddBanner reason={blocked} qty={dest.qty} />}
      {isSurplus ? (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.8rem", marginBottom: "0.4rem" }}>
            <Badge bg="#a8882b">SURPLUS</Badge>
            <span style={{ color: "#e8d28e", fontSize: "1.2rem", fontWeight: 700 }}>HOLD FOR ALLOCATION</span>
          </div>
          <div style={{ fontSize: "5.8rem", fontWeight: 800, color: "#ffeeb0", fontFamily: "monospace", lineHeight: 1 }}>
            {dest.qty}
          </div>
          <div style={{ fontSize: "1.5rem", color: "#d9cc8a", marginTop: "0.3rem" }}><SurplusWhere prefix="pcs to " /></div>
          {assemblyNote(dest.qty, part) && (
            <div style={{ fontSize: "1rem", color: "#b9ad75" }}>{assemblyNote(dest.qty, part)}</div>
          )}
          <SurplusCardBody bundles={dest.bundles} />
        </>
      ) : (
        <>
          {/* WIP staging banner — shown at the very top of the card
              when this destination is a WIP-staging pallet for a
              multi-step part. Operator needs to know the parts going
              into this pallet aren't shipping-ready; they're staging
              for the next workstation (Brake/Fab/Tap/etc.). Same
              wording the routing-slip PDF prints. */}
          {(dest.is_wip || isHoldingDestination(dest)) && (
            <div
              style={{
                background: "#5a4a16",
                border: "2px solid #d39e00",
                borderRadius: 8,
                padding: "0.55rem 0.9rem",
                marginBottom: "0.3rem",
                color: "#ffd969",
                fontWeight: 700,
                fontSize: "1.15rem",
                letterSpacing: 0.5,
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
              }}
            >
              {isHoldingDestination(dest) ? (
                <span data-testid="holding-card-label">{holdingDestinationLabel(dest)}</span>
              ) : (
                <>
                  <span>⚠ WIP STAGING</span>
                  {dest.wip_next_station && (
                    <span style={{ fontSize: "1rem", fontWeight: 600, color: "#ffeab0" }}>
                      · STAGE FOR {String(dest.wip_next_station).toUpperCase()}
                    </span>
                  )}
                </>
              )}
              {/* A ticked part (jobq-api brake_sheet_dividers: its box + Brake after Laser) keeps its sheets carded
                  from the laser on, whichever station its pallet waits for first (Laser, Tap, Brake waits at Tap). */}
              {part?.brake_sheet_dividers && (
                <span style={{ marginLeft: "auto", fontSize: "1rem", fontWeight: 800, color: "#ff8f8f" }}>
                  CARDBOARD BETWEEN SHEETS — DO NOT MIX SHEETS
                </span>
              )}
            </div>
          )}
          {/* Pallet row */}
          {(() => {
            const palletDims = (dest.pallet_length_in && dest.pallet_width_in)
              ? `${Math.round(dest.pallet_length_in)}″ × ${Math.round(dest.pallet_width_in)}″`
              : null;
            const containerNoun = dest.is_pallet_direct ? "Stack" : "Box";
            // Container type — when 'Box' the shipment leaves the dock
            // as a single loose carton, no wood pallet. Drives the
            // BOX-NO-PALLET indicator and changes the operator's
            // mental model (don't grab a pallet from the rack).
            const isBoxContainer = dest.pallet_container_type === "Box";
            return (
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.7rem", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "1.8rem", fontWeight: 700, fontFamily: "monospace", color: "#e6c200" }}>
                    {dest.pallet_short_id || `P${dest.pallet_num}`}
                  </span>
                  <span style={{ fontSize: "1.4rem", color: "#4ec9ff", fontFamily: "monospace", fontWeight: 700 }}>
                    · {dest.dest_so_number}
                  </span>
                  <span style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                    <span style={{ display: "flex", gap: "0.4rem" }}>
                      <PalletBadge dest={dest} />
                      <AllocatedBadge dest={dest} />
                      {isBoxContainer && (
                        <Badge bg="#0d6efd">BOX — NO PALLET</Badge>
                      )}
                      {/* Oversize pallet indicator — anything beyond the
                          standard 40×48 GMA pallet (48×96 or 60×120).
                          Operator needs a different forklift approach
                          + dedicated floor space for these. */}
                      {(() => {
                        const L = +(dest.pallet_length_in || 0);
                        const W = +(dest.pallet_width_in || 0);
                        const isOversize = !isBoxContainer
                          && (Math.max(L, W) > 49 || Math.min(L, W) > 41);
                        return isOversize
                          ? <Badge bg="#dc3545">OVERSIZE</Badge>
                          : null;
                      })()}
                    </span>
                    {palletDims && !isBoxContainer && (
                      <span style={{ fontSize: "0.95rem", color: "#9aa4b4", fontFamily: "monospace" }}>
                        Pallet: {palletDims}
                      </span>
                    )}
                  </span>
                </div>
                <BeaconChip dest={dest} />
                <PalletScanChip dest={dest} />
                {/* Box / Stack row */}
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.6rem", flexWrap: "wrap", marginTop: "0.1rem" }}>
                  <span style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "monospace", color: "#e6c200" }}>
                    {dest.box_short_id || `${dest.is_pallet_direct ? 'S' : 'B'}${dest.box_num}`}
                  </span>
                  {dest.is_pallet_direct && (
                    <span style={{ fontSize: "0.95rem", color: "#9aa4b4", fontFamily: "monospace" }}>STACK</span>
                  )}
                  {(() => {
                    // A box packed inside another order's carton: pack the pieces into that carton.
                    const destPlan = plansBySo && dest.dest_so_id != null ? plansBySo[String(dest.dest_so_id)] : null;
                    const planBox = destPlan
                      ? [...(destPlan.pallets || []).flatMap((p) => p.boxes || []), ...(destPlan.outgoing_ride_alongs || [])]
                          .find((b) => b.box_id === dest.box_id)
                      : null;
                    if (!planBox || !isNested(planBox)) return null;
                    return (
                      <span style={{ fontSize: "1.1rem", color: "#ffd76b", fontFamily: "monospace", fontWeight: 700 }}>
                        {insideText(planBox).toUpperCase()}
                      </span>
                    );
                  })()}
                  {dest.box_sku && (
                    <span style={{ fontSize: "1.1rem", color: "#bcd4ff" }}>
                      {dest.box_sku}
                    </span>
                  )}
                  {/* Revision badge — Rev N marks how many times this
                      box has been opened/rebalanced. Matters when the
                      office issued a replan mid-cut and operator needs
                      to be sure they're working with the latest layout. */}
                  {dest.box_revision_opened > 1 && (
                    <span
                      style={{
                        fontSize: "0.85rem",
                        background: "#3a2e0c",
                        color: "#ffd76b",
                        padding: "0.15rem 0.45rem",
                        borderRadius: 4,
                        fontFamily: "monospace",
                        fontWeight: 700,
                      }}
                      title={`This box has been revised ${dest.box_revision_opened} time(s). Make sure you're working from the latest routing slip.`}
                    >
                      REV {dest.box_revision_opened}
                    </span>
                  )}
                  <span style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                    <BoxBadge dest={dest} />
                    {dims && (
                      <span style={{ fontSize: "0.95rem", color: "#9aa4b4", fontFamily: "monospace" }}>
                        {containerNoun}: {dims}
                      </span>
                    )}
                  </span>
                </div>
                {posText && (
                  <div style={{ fontSize: "0.95rem", color: "#8fa3b8" }}>
                    Position on pallet: {posText}
                  </div>
                )}
                {/* THE big QUANTITY */}
                <div style={{ marginTop: "0.35rem", display: "flex", alignItems: "baseline", gap: "0.8rem" }}>
                  <span style={{ fontSize: "1.25rem", color: "#8fa3b8", fontWeight: 600 }}>QUANTITY:</span>
                  <span style={{ fontSize: "5.8rem", fontWeight: 800, color: blocked ? "#ffb3bb" : "white", fontFamily: "monospace", lineHeight: 1 }}>
                    {dest.qty}
                  </span>
                  <span style={{ fontSize: "1.6rem", color: "#c9d4e2", fontWeight: 700 }}>pcs</span>
                </div>
                {assemblyNote(dest.qty, part) && (
                  <div style={{ fontSize: "1rem", color: "#9aa4b4" }}>{assemblyNote(dest.qty, part)}</div>
                )}
                {dest.stack_phrase && (
                  <div style={{ fontSize: "1.15rem", color: "#bcd4ff", fontWeight: 600 }}>
                    ({dest.stack_phrase}
                    {dest.stack_layout && dest.stack_layout.qty
                      ? ` = ${unitWord(dest.stack_layout.qty, dest.stack_units === "assemblies")} on the stack`
                      : ""})
                  </div>
                )}
                {/* Pack summary */}
                <div style={{ fontSize: "1.15rem", color: "#e8f2ea", marginTop: "0.3rem" }}>
                  {containerNoun} status after pack: <b>{dest.new_box_status || "—"}</b>
                </div>
                {dest.part_planned_total > 0 && (
                  <div style={{ fontSize: "1rem", color: "#bcd4ff" }}>
                    When {containerNoun.toLowerCase()} is full: {dest.part_planned_total} pcs of {part.product_id}
                  </div>
                )}
                {/* Pallet timeline — surface the most recent state
                    transition so the operator can tell at a glance
                    "this pallet was sealed 2h ago" vs "just sealed."
                    Picked-up trumps shipped, shipped trumps staged,
                    staged trumps sealed — show whichever's the
                    furthest-along state with its actor + relative
                    time. */}
                {(() => {
                  const events = [
                    dest.pallet_picked_up_at && {
                      label: "Picked up",
                      at: dest.pallet_picked_up_at,
                      by: dest.pallet_picked_up_by,
                      color: "#7ede9e",
                    },
                    dest.pallet_shipped_at && {
                      label: "Shipped",
                      at: dest.pallet_shipped_at,
                      by: dest.pallet_shipped_by,
                      color: "#7ede9e",
                    },
                    dest.pallet_staged_at && {
                      label: "Staged for pickup",
                      at: dest.pallet_staged_at,
                      by: dest.pallet_staged_by,
                      color: "#bcd4ff",
                    },
                    dest.pallet_sealed_at && {
                      label: "Sealed",
                      at: dest.pallet_sealed_at,
                      by: dest.pallet_sealed_by,
                      color: "#9aa4b4",
                    },
                  ].filter(Boolean);
                  if (events.length === 0) return null;
                  const e = events[0];
                  const rel = fmtRelTime(e.at);
                  return (
                    <div style={{ fontSize: "0.9rem", color: e.color, marginTop: "0.2rem", fontStyle: "italic" }}>
                      {e.label} {rel || ""}
                      {e.by ? ` by ${e.by}` : ""}
                    </div>
                  );
                })()}
              </>
            );
          })()}
          {dest.part_needs_more > 0 && (
            <div style={{ fontSize: "1.15rem", color: "#ffd969", fontStyle: "italic", fontWeight: 600 }}>
              Needs {dest.part_needs_more} more pcs
              {dest.expected_from_job && ` · Expected from Job ${dest.expected_from_job}`}
            </div>
          )}
          {dest.dest_customer_name && (
            <div style={{ fontSize: "1.05rem", color: "#bcd4ff", paddingTop: "0.35rem" }}>
              Route to: <b>{dest.dest_so_number}</b> · {dest.dest_customer_name}
              {dest.dest_tag === "Allocated" && " (Allocated)"}
            </div>
          )}
          {/* Cross-plan ride-along — this box's planning home is the
              dest_so above, but it physically ships on another SO's
              pallet (same customer). Operators need to know the box
              ends up on the host pallet at ship time. May 2026
              ride-along workflow. */}
          {dest.riding_to_so_number && (
            <div
              style={{
                fontSize: "1rem",
                background: "#1a3a52",
                border: "1.5px solid #4ec9ff",
                borderRadius: 8,
                padding: "0.4rem 0.7rem",
                marginTop: "0.3rem",
                color: "#bcd4ff",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 700, color: "#4ec9ff" }}>↪ RIDES ON</span>
              <span style={{ fontFamily: "monospace", fontWeight: 700 }}>
                {dest.riding_to_so_number}
              </span>
              {dest.riding_to_pallet_short_id && (
                <span style={{ fontFamily: "monospace", color: "#e6c200" }}>
                  [{dest.riding_to_pallet_short_id}]
                </span>
              )}
              <span style={{ fontSize: "0.9rem", color: "#8fa3b8" }}>
                — physical ship pallet
              </span>
            </div>
          )}
          {/* Graphics row: pallet map thumb + box interior thumb, click to enlarge */}
          {(() => {
            const destPlan = plansBySo && dest.dest_so_id != null
              ? plansBySo[String(dest.dest_so_id)] : null;
            const boxOnPlan = destPlan
              ? (destPlan.pallets || [])
                  .flatMap((p) => p.boxes || [])
                  .find((b) => b.box_id === dest.box_id)
              : null;
            if (!destPlan && !boxOnPlan) return null;
            return (
              // Both thumbs shrink (in proportion to their natural width) to fit
              // the card; at fixed scale a multi-pallet map + a wide stack ran
              // past the card's right edge.
              <div style={{ display: "flex", gap: "0.6rem", marginTop: "auto", paddingTop: "0.5rem", alignItems: "flex-start", minWidth: 0, maxWidth: "100%" }}>
                {destPlan && (
                  <div
                    onClick={() => onEnlarge && onEnlarge({
                      kind: "pallet",
                      plan: destPlan,
                      highlightPalletId: dest.pallet_id,
                      highlightBoxId: dest.box_id,
                      outlineCache,
                      title: `Pallet map — SO-${dest.dest_so_number}`,
                    })}
                    style={{
                      cursor: "pointer",
                      background: "#222831",
                      padding: 4,
                      borderRadius: 6,
                      border: "1px solid #3a4050",
                      flex: "0 1 auto",
                      minWidth: 0,
                    }}
                    title="Click to enlarge"
                  >
                    <div style={{ fontSize: "0.75rem", color: "#8fa3b8", marginBottom: 2, textAlign: "center" }}>
                      Pallet map
                    </div>
                    <PalletMapSVG
                      plan={destPlan}
                      highlightPalletId={dest.pallet_id}
                      highlightBoxId={dest.box_id}
                      outlineCache={outlineCache}
                      scalePxPerIn={2.8}
                    />
                  </div>
                )}
                {boxOnPlan && (
                  <div
                    onClick={() => onEnlarge && onEnlarge({
                      kind: "box",
                      box: boxOnPlan,
                      highlightPartId: part.part_id,
                      title: `${dest.is_pallet_direct ? "Stack" : "Box"} ${dest.box_short_id || `${dest.is_pallet_direct ? "S" : "B"}${dest.box_num}`}${dest.box_sku ? ` — ${dest.box_sku}` : ""}`,
                    })}
                    style={{
                      cursor: "pointer",
                      background: "#fafafa",
                      padding: 4,
                      borderRadius: 6,
                      border: "1px solid #3a4050",
                      flex: "0 1 auto",
                      minWidth: 0,
                    }}
                    title="Click to enlarge"
                  >
                    <div style={{ fontSize: "0.75rem", color: "#555", marginBottom: 2, textAlign: "center", fontWeight: 600 }}>
                      {dest.is_pallet_direct ? "Stack" : "Box"} interior
                    </div>
                    <BoxInteriorSVG
                      box={boxOnPlan}
                      highlightPartId={part.part_id}
                      outlineCache={outlineCache}
                      scalePxPerIn={5.5}
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

// ─── Selected-part view: image sidebar + destinations grid ────────────
function PartView({ part, source, plansBySo, outlineCache, onEnlarge }) {
  if (!part) return null;
  const mat = [part.material_type, part.display_thickness, part.surface_finish]
    .filter(Boolean)
    .join(" · ");
  const placed = (part.destinations || []).reduce(
    (a, d) => a + (d.kind === "packaging" && !doNotAddReason(d) ? (d.qty || 0) : 0), 0);
  const blockedPcs = (part.destinations || []).reduce(
    (a, d) => a + (doNotAddReason(d) ? (d.qty || 0) : 0), 0);
  const surplus = (part.destinations || []).reduce(
    (a, d) => a + (d.kind === "surplus" ? (d.qty || 0) : 0), 0);
  const owed = (part.destinations || []).reduce(
    (a, d) => a + (d.kind === "owed" ? (d.qty || 0) : 0), 0);
  const size = (part.size_length && part.size_width)
    ? `${part.size_length}″ × ${part.size_width}″` : null;
  const weight = part.weight_lbs ? `${part.weight_lbs.toFixed(1)} lbs ea` : null;
  // Assembly identity — when this laser part is the primary of an
  // assembly being shipped on the active SO, the operator-facing
  // identity is the assembly (e.g. "MEB CRIBBING × 100") not the raw
  // primary part (e.g. "MEBDIVIDER × 400"). Mirrors the SO card
  // (SOPlannedBoxCard) ASSY badge + ppq-divided display.
  const isAssembly = !!(part.assembly_id && part.assembly_name);
  const ppq = Math.max(1, parseInt(part.primary_part_quantity, 10) || 1);
  // Every count here is in PIECES, like the destination cards (what comes off the
  // sheet). An assembly primary adds "= N assemblies" under the job's total.
  const order = shownFillOrder(part.destinations);
  const orderedDests = destinationsInFillOrder(part.destinations);

  // As-packed hero drawing: render the same DXF outline the packing diagrams
  // use, rotated to how the part actually sits in its container, so the big
  // reference matches the Pallet/Stack thumbnails below it instead of showing
  // the raw CAD orientation. We orient to the first packaging destination's
  // zone (head-on, like the "Stack interior" thumbnail). Falls back to the
  // native photo when no outline/destination is available; the photo is always
  // one tap away via the enlarge modal.
  const heroDest = (part.destinations || []).find(
    (d) => d.kind === "packaging" && d.box_id != null && d.dest_so_id != null
  );
  const heroPlan = (heroDest && plansBySo) ? plansBySo[String(heroDest.dest_so_id)] : null;
  const heroBox = heroPlan
    ? (heroPlan.pallets || []).flatMap((p) => p.boxes || []).find((b) => b.box_id === heroDest.box_id)
    : null;
  const heroZone = heroBox
    ? (heroBox.zones || []).find((z) => z.part_id === part.part_id)
    : null;
  const heroOutlinePath = (heroZone && heroZone.outline_svg_path) || part.outline_svg_path;
  const heroOutline = (outlineCache && heroOutlinePath) ? outlineCache[heroOutlinePath] : null;
  const heroHasOutline = !!(heroOutline && heroOutline.paths && heroOutline.paths.length);
  // Orient the hero to match how the part sits in its container: rotate the
  // outline only when its native aspect is crosswise to the zone footprint
  // (zone_w × zone_h). The packer's z.rotated flag is NOT a reliable proxy —
  // it tracks pallet-space L/W swaps, so for a part whose SVG already matches
  // its footprint (aspect already equal) it would wrongly turn the drawing 90°.
  const heroPartLandscape = heroOutline
    ? (heroOutline.maxX - heroOutline.minX) > (heroOutline.maxY - heroOutline.minY)
    : false;
  const heroCellLandscape = heroZone
    ? Number(heroZone.zone_w) > Number(heroZone.zone_h)
    : heroPartLandscape;
  const heroRotated = heroPartLandscape !== heroCellLandscape;

  return (
    <div style={{ display: "flex", gap: "1rem", flex: 1, minHeight: 0 }}>
      {/* Sidebar: dominant part image + part info. ~32% of viewport width
          on wide screens, caps at 440px. Sticky so it stays visible as
          the destinations scroll. */}
      <div
        style={{
          width: "32%",
          maxWidth: 440,
          minWidth: 280,
          background: "#1a1d24",
          borderRadius: 14,
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.8rem",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* THE part image. Click to enlarge. */}
        <div
          onClick={() => part.image_path && onEnlarge && onEnlarge({
            kind: "image",
            url: part.image_path,
            title: part.product_id,
          })}
          style={{
            width: "100%",
            aspectRatio: "1 / 1",
            background: (heroHasOutline || part.image_path) ? "#fafafa" : "#2a2f3a",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            cursor: part.image_path ? "pointer" : "default",
          }}
        >
          {heroHasOutline ? (
            <PartOutlineImage data={heroOutline} rotated={heroRotated} />
          ) : part.image_path ? (
            <img
              src={part.image_path}
              alt={part.product_id}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div style={{ color: "#6c757d", fontSize: "1.1rem", fontStyle: "italic" }}>
              (no image)
            </div>
          )}
        </div>

        {/* Part info — assembly-aware. When this part is the primary
            of a finished assembly being shipped, the assembly name
            takes top billing and the raw primary part_id sits below
            as a "primary part" secondary line. Mirrors the SO card. */}
        {isAssembly ? (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.55rem", flexWrap: "wrap" }}>
              <Badge bg="#28a745">ASSEMBLY</Badge>
              {ppq > 1 && (
                <span style={{ color: "#9aa4b4", fontSize: "0.9rem", letterSpacing: 0.5 }}>
                  {ppq}× per assembly
                </span>
              )}
            </div>
            <div style={{ color: "white", fontSize: "1.8rem", fontWeight: 700, wordBreak: "break-word", lineHeight: 1.1 }}>
              {part.assembly_name}
            </div>
            <div style={{ color: "#9aa4b4", fontSize: "1rem" }}>
              Primary:{" "}
              <span style={{ color: "#bcd4ff", fontFamily: "monospace", fontWeight: 700 }}>
                {part.product_id}
              </span>
            </div>
          </>
        ) : (
          <div style={{ color: "white", fontSize: "2rem", fontFamily: "monospace", fontWeight: 700, wordBreak: "break-word", lineHeight: 1.1 }}>
            {part.product_id}
          </div>
        )}
        {part.description && (
          <div style={{ color: "#bcd4ff", fontSize: "1.2rem", lineHeight: 1.3 }}>
            {part.description}
          </div>
        )}
        {mat && (
          <div style={{ color: "#e8d28e", fontSize: "1.05rem" }}>{mat}</div>
        )}
        {(size || weight) && (
          <div style={{ color: "#9aa4b4", fontSize: "1rem" }}>
            {[size, weight].filter(Boolean).join(" · ")}
          </div>
        )}

        {/* Big progress numbers, all in pieces (the destination cards count pieces too). */}
        <div style={{ marginTop: "auto", background: "#0f1115", padding: "0.8rem", borderRadius: 8 }}>
          {[
            ["On this job", part.qty_ordered, "white"],
            ["To pallets", placed, "#7ede9e"],
            owed > 0 && ["Owed, no spot", owed, "#ff8f7a"],
            surplus > 0 && ["Surplus", surplus, "#ffd969"],
            blockedPcs > 0 && ["DO NOT ADD", blockedPcs, "#ff6b7a"],
          ].filter(Boolean).map(([label, n, color], i) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: i ? 4 : 0 }}>
              <span style={{ color: "#8fa3b8", fontSize: "1rem" }}>{label}</span>
              <span style={{ color, fontSize: "1.8rem", fontWeight: 700, fontFamily: "monospace" }}>
                {n} <span style={{ fontSize: "1rem", color: "#8fa3b8" }}>pcs</span>
              </span>
            </div>
          ))}
          {isAssembly && (
            <div style={{ marginTop: "0.5rem", paddingTop: "0.4rem", borderTop: "1px solid #2a2f3a", fontSize: "0.9rem", color: "#9aa4b4" }}>
              {part.qty_ordered} pcs {assemblyNote(part.qty_ordered, part)}
            </div>
          )}
        </div>
      </div>

      {/* Destinations grid — takes remaining width, scrolls */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(400px, 1fr))`,
          // rows as tall as their card: in this flex: 1 scroll box the auto rows came out at the cards' 360px minimum,
          // so a taller card (the surplus crate steps, a pallet card's maps) spilled over the next one (2026-11-04)
          gridAutoRows: "max-content",
          gap: "0.9rem",
          flex: 1,
          overflowY: "auto",
          alignContent: "start",
        }}
      >
        {part.brake_sheet_dividers && (part.destinations || []).length > 0 && (
          <div
            style={{
              gridColumn: "1 / -1",
              background: "#4a1216",
              border: "2px solid #ff6b6b",
              borderRadius: 8,
              padding: "0.5rem 0.9rem",
              color: "#ff8f8f",
              fontWeight: 800,
              fontSize: "1.15rem",
              letterSpacing: 0.5,
            }}
          >
            ⚠ CARDBOARD BETWEEN SHEETS — cover each sheet's parts with cardboard, its label on top; each sheet has its own brake setup
          </div>
        )}
        {(part.destinations || []).length === 0 ? (
          part.next_station ? (
            // Multi-step part — laser is just one stop on the route.
            // The package guide doesn't auto-route these to a shipping
            // pallet because the post-laser dims aren't known yet; the
            // shop's WIP rack absorbs them until brake/fab/etc. finishes.
            <div
              style={{
                background: "#2e2410",
                border: "2px solid #b8860b",
                borderRadius: 12,
                padding: "1.4rem 1.6rem",
                color: "#ffd969",
                display: "flex",
                flexDirection: "column",
                gap: "0.6rem",
              }}
            >
              <div style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: 0.5 }}>
                ROUTE TO {part.next_station.toUpperCase()}
              </div>
              <div style={{ fontSize: "1.1rem", color: "#ffe9a8", lineHeight: 1.35 }}>
                Stage on the WIP rack — these parts still need
                {" "}
                <strong>{part.next_station}</strong>
                {" "}
                before they're shipping-ready.
              </div>
              {part.brake_sheet_dividers && (
                <div
                  style={{
                    background: "#4a1216",
                    border: "2px solid #ff6b6b",
                    borderRadius: 8,
                    padding: "0.6rem 0.9rem",
                    color: "#ffb3b3",
                    fontSize: "1.05rem",
                    lineHeight: 1.4,
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#ff8f8f", fontSize: "1.25rem", letterSpacing: 0.5 }}>
                    CARDBOARD BETWEEN SHEETS
                  </div>
                  Never mix sheets — each sheet needs its own brake setup.
                  Stack the sheets in order: cover each sheet's parts with
                  cardboard and stick that sheet's printed label on it.
                </div>
              )}
              {part.process_steps && (
                <div style={{ fontSize: "0.95rem", color: "#c9aa5a", fontFamily: "monospace" }}>
                  Process: {part.process_steps}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "#6c757d", fontSize: "1.5rem", fontStyle: "italic", padding: "2rem" }}>
              No destinations routed for this part yet.
            </div>
          )
        ) : (
          orderedDests.map((d, i) => (
            <DestinationCard
              key={`${d.kind}-${d.box_id || `s${i}`}`}
              dest={d}
              order={order.get(d)}
              part={part}
              plansBySo={plansBySo}
              outlineCache={outlineCache}
              onEnlarge={onEnlarge}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Part switcher: "All parts" (the unload view) + one tab per part ───
const partTabStyle = (active) => ({
  background: active ? "#0d6efd" : "#1c1f26",
  color: active ? "white" : "#bcd4ff",
  border: active ? "3px solid #4ec9ff" : "3px solid #2a2f3a",
  borderRadius: 12,
  padding: "0.6rem 1.2rem",
  fontSize: "1.25rem",
  fontWeight: 700,
  fontFamily: "monospace",
  cursor: "pointer",
  whiteSpace: "nowrap",
  minWidth: 160,
  textAlign: "left",
});

function PartTabs({ parts, activePartId, onSelect }) {
  if (!parts || parts.length <= 1) return null;
  const all = activePartId === ALL_PARTS;
  return (
    <div style={{ display: "flex", gap: "0.5rem", overflowX: "auto", paddingBottom: "0.2rem", flexShrink: 0 }}>
      <button type="button" data-testid="part-tab-all" onClick={() => onSelect(ALL_PARTS)} style={partTabStyle(all)}>
        <div>ALL PARTS</div>
        <div style={{ fontSize: "0.9rem", fontWeight: 500, marginTop: 2, opacity: 0.85 }}>
          {parts.length} on this sheet: unload together
        </div>
      </button>
      {parts.map((p) => {
        const active = p.part_id === activePartId;
        const placed = (p.destinations || []).reduce(
          (a, d) => a + (d.kind === "packaging" && !doNotAddReason(d) ? (d.qty || 0) : 0), 0);
        const isAssembly = !!(p.assembly_id && p.assembly_name);
        return (
          <button key={p.part_id} type="button" onClick={() => onSelect(p.part_id)} style={partTabStyle(active)}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              {isAssembly && (
                <span style={{
                  background: "#28a745", color: "white",
                  fontSize: "0.65rem", fontWeight: 700, letterSpacing: 0.5,
                  padding: "0.1rem 0.35rem", borderRadius: 3,
                  fontFamily: "sans-serif",
                }}>ASSY</span>
              )}
              <span>{isAssembly ? p.assembly_name : p.product_id}</span>
            </div>
            <div style={{ fontSize: "0.9rem", fontWeight: 500, marginTop: 2, opacity: 0.85 }}>
              {placed}/{p.qty_ordered} pcs to pallets
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Recommended packaging body (internal jobs, May 2026) ────────────
// Renders the loose operator-guidance recommendation produced by
// `cut_sheet_packaging.py` for internal-job laser cut sheets. Not a
// committed plan — no sealing, no traceability. Just instructions for
// stacking parts safely on pallets / totes.
function summarizeStacks(stacks) {
  if (!stacks || stacks.length === 0) return "—";
  if (stacks.length === 1) {
    const c = stacks[0].count;
    return `1 stack of ${c}`;
  }
  // Group by count for compact display: "2 stacks of 7" or "3 stacks of 4 + 1 stack of 3"
  const buckets = stacks.reduce((acc, s) => {
    acc[s.count] = (acc[s.count] || 0) + 1;
    return acc;
  }, {});
  const parts = Object.entries(buckets)
    .sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(([count, n]) => `${n} stack${n > 1 ? "s" : ""} of ${count}`);
  return parts.join(" + ");
}

function RecommendedPackagingBody({ recommendation, task }) {
  if (!recommendation) return null;
  const pallets = recommendation.pallets || [];
  const totes = recommendation.totes || [];
  // V3.3: render operator-typed notes from cut_sheets.packaging_notes
  // in the kiosk's "Notes" block. The system-generated `warnings`
  // array (stale-layout flags etc.) is suppressed on the kiosk —
  // those still show in the cut-sheet entry editor where the office
  // can act on them.
  const operatorNotes = (recommendation.operator_notes || "").trim();
  const cutSheetName =
    task?.cut_sheet_name ||
    (recommendation.cut_sheet_id ? `#${recommendation.cut_sheet_id}` : "—");

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: "0.7rem",
        background: "#14171d",
        borderRadius: 12,
        border: "1px solid #2a2f3a",
        padding: "0.9rem 1rem",
        overflowY: "auto",
      }}
    >
      {/* Header strip */}
      <div style={{ display: "flex", alignItems: "baseline", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ fontSize: "1.7rem", fontWeight: 700, color: "#bcd4ff" }}>
          Recommended packaging
        </div>
        <div style={{ fontSize: "1.1rem", color: "#9aa4b4" }}>
          Cut Sheet {cutSheetName}
          {recommendation.sheet_count > 1 ? ` · ${recommendation.sheet_count} sheets` : ""}
        </div>
        <div
          style={{
            fontSize: "0.95rem",
            fontWeight: 700,
            color: "#0f1115",
            background: "#ffd76b",
            padding: "0.15rem 0.6rem",
            borderRadius: 6,
            letterSpacing: 0.5,
          }}
        >
          ADVISORY · NOT ENFORCED
        </div>
      </div>

      {/* Pallets */}
      {pallets.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
            gap: "0.8rem",
          }}
        >
          {pallets.map((pal) => (
            <RecommendedPalletCard key={pal.pallet_num} pallet={pal} />
          ))}
        </div>
      )}

      {/* Totes */}
      {totes.length > 0 && (
        <>
          <div style={{ marginTop: pallets.length > 0 ? "0.4rem" : 0, fontSize: "1.15rem", fontWeight: 700, color: "#9aa4b4", letterSpacing: 0.5 }}>
            TOTES (small parts, one per tote)
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "0.6rem",
            }}
          >
            {totes.map((t) => (
              <RecommendedToteCard key={t.tote_num} tote={t} />
            ))}
          </div>
        </>
      )}

      {/* Operator notes (V3.3) — free-text from cut_sheets.packaging_notes.
          Replaces the system warnings on the kiosk; the office sees
          warnings in the cut-sheet entry editor instead. Multi-line
          friendly with whitespace-pre-wrap so newlines render. */}
      {operatorNotes && (
        <div
          style={{
            marginTop: "0.4rem",
            background: "#1d2738",
            border: "1px solid #3a5a8a",
            borderRadius: 8,
            padding: "0.7rem 0.9rem",
            color: "#cfe1ff",
            fontSize: "1.05rem",
            lineHeight: 1.4,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4, color: "#9bb8e8", fontSize: "0.85rem", letterSpacing: 0.5 }}>
            NOTES
          </div>
          <div style={{ whiteSpace: "pre-wrap" }}>{operatorNotes}</div>
        </div>
      )}
    </div>
  );
}

// Pastel palette mirroring SOPackagingPlanCard's SO_PKG_COLOR_PALETTE so
// the same operator can recognize parts across the SO plan UI and the
// kiosk advisory view at a glance.
const REC_PART_COLORS = [
  "rgb(209, 227, 255)", "rgb(219, 247, 209)", "rgb(255, 222, 179)",
  "rgb(240, 204, 255)", "rgb(255, 247, 191)", "rgb(199, 250, 242)",
  "rgb(255, 204, 217)", "rgb(219, 219, 235)",
];

// Compute a per-pallet weight estimate from the parts on it.
// Each stack contributes (count × weight_lbs).
function estimatePalletWeight(pallet) {
  let lbs = 0;
  for (const p of pallet.parts || []) {
    const w = Number(p.weight_lbs) || 0;
    if (!w) continue;
    for (const s of p.stacks || []) {
      lbs += (s.count || 0) * w;
    }
  }
  return lbs;
}

// Lay out parts on the pallet in shelves along the pallet width axis.
// Single-part pallets render at the native (x, y) positions from the
// backend layout. Mingled pallets stack each part's tile group below the
// previous one (cursor advances along pallet width). Overflow is allowed
// — operators stack physically beyond a single-layer footprint anyway —
// and an `overflow` flag lets the renderer warn.
function laidOutParts(pallet) {
  // V2.1 onwards: backend lays out multi-part pallets via shelf-pack so
  // each stack already has its final (x, y) on the pallet. We just trust
  // those positions and check for overflow per-stack. Legacy responses
  // without positional fields fall back to a simple grid (col*tileL,
  // row*tileW) — the cumulative is_mingled cursor is gone since it
  // double-shifted the new shelf-packed positions.
  const palletL = Number(pallet.length_in) || 48;
  const palletW = Number(pallet.width_in) || 40;
  let overflow = false;
  const out = (pallet.parts || []).map((p, idx) => {
    const tileL = Number(p.tile_l) || 0;
    const tileW = Number(p.tile_w) || 0;
    const cols = Math.max(1, Number(p.tile_cols) || 1);
    const rows = Math.max(1, Number(p.tile_rows) || 1);
    const stacks = (p.stacks || []).map((s, si) => {
      const c = Number.isFinite(s.col) ? s.col : si % cols;
      const r = Number.isFinite(s.row) ? s.row : Math.floor(si / cols);
      const x = Number.isFinite(s.x) ? s.x : c * tileL;
      const y = Number.isFinite(s.y) ? s.y : r * tileW;
      if (x + tileL > palletL + 0.5 || y + tileW > palletW + 0.5) {
        overflow = true;
      }
      return { ...s, col: c, row: r, x, y };
    });
    return {
      ...p,
      _layout: { tileL, tileW, cols, rows },
      stacks,
    };
  });
  return { parts: out, overflow, palletL, palletW };
}

// Pallet rendered in PORTRAIT (operator's view from a narrow forklift-access
// edge): SVG width = palletW (40), SVG height = palletL (48). Backend
// returns positions where x is along palletL and y is along palletW; we
// remap each stack to: rotatedX = backend.y, rotatedY = palletL - backend.x
// - tile_l, so the original "left edge" (backend x=0) lands at the BOTTOM
// of the SVG (closest forklift-access edge to the operator) and "right edge"
// (backend x=palletL-tile_l) lands at the TOP.
function RecommendedPalletMap({ pallet }) {
  const SCALE = 4.5; // px per inch
  const PAD_LEFT = 28;   // room for the "48"" length label rotated on the left
  const PAD_BOTTOM = 22; // room for the "40"" width label below
  const { parts, palletL, palletW } = laidOutParts(pallet);

  const palletPxW = palletW * SCALE;
  const palletPxH = palletL * SCALE;
  const svgW = palletPxW + PAD_LEFT + 4;
  const svgH = palletPxH + PAD_BOTTOM + 4;

  // Top-left corner of the pallet rect inside the SVG
  const px0 = PAD_LEFT;
  const py0 = 2;

  // Pallet visual: dark warm wood-tone fill, faint deck-board lines,
  // small fork-pocket cut-ins on the narrow ends (top + bottom of portrait).
  const deckBoardCount = 6;
  const deckGap = palletPxH / deckBoardCount;
  const forkPocketDepth = 14;       // px deep
  const forkPocketWidth = palletPxW * 0.22;
  const forkPocketInset = palletPxW * 0.18;

  return (
    <svg
      width={svgW}
      height={svgH}
      style={{
        background: "#0e1014",
        border: "1px solid #3a4150",
        borderRadius: 4,
        flexShrink: 0,
      }}
    >
      {/* Pallet base — wood tone */}
      <rect
        x={px0} y={py0}
        width={palletPxW} height={palletPxH}
        fill="#2a2218"
        stroke="#5a4630" strokeWidth={1.2}
      />
      {/* Deck-board lines (run along the long axis = vertical in portrait) */}
      {Array.from({ length: deckBoardCount - 1 }).map((_, i) => {
        const y = py0 + (i + 1) * deckGap;
        return (
          <line
            key={`db${i}`}
            x1={px0 + 2} y1={y}
            x2={px0 + palletPxW - 2} y2={y}
            stroke="#3d2f1f" strokeWidth={0.8}
          />
        );
      })}
      {/* Fork pockets on top + bottom narrow edges */}
      {[0, palletPxH - forkPocketDepth].map((yOff, edgeIdx) => (
        <g key={`fp${edgeIdx}`}>
          <rect
            x={px0 + forkPocketInset} y={py0 + yOff}
            width={forkPocketWidth} height={forkPocketDepth}
            fill="#0e1014"
            stroke="#5a4630" strokeWidth={0.6}
          />
          <rect
            x={px0 + palletPxW - forkPocketInset - forkPocketWidth} y={py0 + yOff}
            width={forkPocketWidth} height={forkPocketDepth}
            fill="#0e1014"
            stroke="#5a4630" strokeWidth={0.6}
          />
        </g>
      ))}

      {/* Stacks — re-mapped from backend (x,y) to portrait coords */}
      {parts.map((p, idx) => {
        const color = REC_PART_COLORS[(p.color_idx ?? idx) % REC_PART_COLORS.length];
        const tileL = p._layout.tileL || 0;
        const tileW = p._layout.tileW || 0;
        return (
          <g key={p.part_id}>
            {(p.stacks || []).map((s, si) => {
              const origX = s.x || 0;
              const origY = s.y || 0;
              // Portrait remap: x goes to (palletW - origY - tileW) so the
              // stack stays glued to the same edge after rotation; y flips so
              // backend x=0 (left in landscape) → bottom of portrait.
              const rectW = tileW * SCALE;
              const rectH = tileL * SCALE;
              const sx = px0 + origY * SCALE;
              const sy = py0 + (palletL - origX - tileL) * SCALE;
              const fontSize = Math.min(15, Math.max(9, Math.min(rectW, rectH) / 4));
              const isOversize = s.oversized;
              return (
                <g key={`s${si}`}>
                  <rect
                    x={sx} y={sy} width={rectW} height={rectH}
                    fill={color}
                    fillOpacity={isOversize ? 0.4 : 0.9}
                    stroke="#0e1014" strokeWidth={1.2}
                  />
                  {isOversize && (
                    <rect
                      x={sx} y={sy} width={rectW} height={rectH}
                      fill="none"
                      stroke="#ff6b6b" strokeWidth={1.5} strokeDasharray="3 2"
                    />
                  )}
                  {Math.min(rectW, rectH) >= 20 && (
                    <text
                      x={sx + rectW / 2}
                      y={sy + rectH / 2 + fontSize / 3}
                      textAnchor="middle"
                      fontSize={fontSize}
                      fontWeight={700}
                      fill="#0e1014"
                      style={{ paintOrder: "stroke", stroke: "rgba(255,255,255,0.6)", strokeWidth: 1.2 }}
                    >
                      {s.count}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}

      {/* Dimension labels */}
      {/* Width along the bottom edge */}
      <text
        x={px0 + palletPxW / 2}
        y={py0 + palletPxH + 16}
        textAnchor="middle"
        fontSize={11}
        fill="#9aa4b4"
        fontFamily="monospace"
      >
        {Math.round(palletW)}″
      </text>
      {/* Length rotated -90° on the left edge */}
      <text
        x={12}
        y={py0 + palletPxH / 2}
        textAnchor="middle"
        fontSize={11}
        fill="#9aa4b4"
        fontFamily="monospace"
        transform={`rotate(-90 12 ${py0 + palletPxH / 2})`}
      >
        {Math.round(palletL)}″
      </text>
    </svg>
  );
}

function RecommendedPalletCard({ pallet }) {
  const isShip = pallet.destination_kind === "ship";
  const accent = isShip ? "#7ede9e" : "#ffb74d";
  const bgStripe = isShip ? "rgba(126, 222, 158, 0.10)" : "rgba(255, 183, 77, 0.10)";
  const totalPieces = (pallet.parts || []).reduce((a, p) => a + (p.qty || 0), 0);
  const estWeight = estimatePalletWeight(pallet);
  const palletDims = `${Math.round(pallet.length_in || 48)}×${Math.round(pallet.width_in || 40)}″`;
  const { overflow } = laidOutParts(pallet);

  return (
    <div
      style={{
        background: "#1a1d24",
        border: `2px solid ${accent}`,
        borderRadius: 10,
        padding: "0.8rem 1rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.6rem",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.6rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.6rem" }}>
          <div style={{ fontSize: "1.6rem", fontWeight: 700, color: accent, fontFamily: "monospace" }}>
            {pallet.label}
          </div>
          <div style={{ fontSize: "0.85rem", color: "#9aa4b4" }}>{palletDims}</div>
        </div>
        <div style={{ fontSize: "0.95rem", color: "#9aa4b4", display: "flex", gap: "0.6rem" }}>
          <span>{totalPieces} pcs</span>
          {estWeight > 0 && <span>~{Math.round(estWeight)} lbs</span>}
        </div>
      </div>

      {/* Destination chip */}
      <div
        style={{
          background: bgStripe,
          padding: "0.3rem 0.6rem",
          borderRadius: 6,
          fontSize: "1.05rem",
          fontWeight: 600,
          color: accent,
        }}
      >
        Route to: {pallet.destination}
      </div>

      {/* ONE SHEET PER PALLET chip — brake-bound pallets. Sheet-split
          pallets name their exact sheet; a custom (unsplit) layout warns
          that it shows all sheets combined. */}
      {pallet.one_sheet_per_pallet && (
        <div
          style={{
            background: "#4a1216",
            border: "2px solid #ff6b6b",
            borderRadius: 6,
            padding: "0.35rem 0.6rem",
            fontSize: "1rem",
            fontWeight: 700,
            color: "#ff8f8f",
          }}
        >
          ⚠ ONE SHEET PER PALLET —{" "}
          {pallet.sheet_index
            ? `this pallet = SHEET ${pallet.sheet_index} only`
            : "do not mix sheets"}
          {pallet.sheet_scope === "all_sheets" && (
            <span style={{ fontWeight: 600, color: "#ffb3b3" }}>
              {" "}(custom layout shows ALL sheets combined — repeat it per sheet)
            </span>
          )}
        </div>
      )}

      {/* Pallet map + parts list, side-by-side */}
      <div style={{ display: "flex", gap: "0.9rem", alignItems: "flex-start", flexWrap: "wrap" }}>
        <RecommendedPalletMap pallet={pallet} />
        <div style={{ flex: "1 1 220px", minWidth: 220, display: "flex", flexDirection: "column", gap: "0.45rem" }}>
          {(pallet.parts || []).map((p, idx) => {
            const color = REC_PART_COLORS[(p.color_idx ?? idx) % REC_PART_COLORS.length];
            const dims = (p.size_width && p.size_length)
              ? `${(+p.size_width).toFixed(1)}×${(+p.size_length).toFixed(1)}″`
              : null;
            const isAssemblyComponent = p.assembly_role === "component";
            const isAssemblyPrimary = p.assembly_role === "primary";
            return (
              <div key={p.part_id} style={{
                background: "#22262f",
                borderLeft: `5px solid ${color}`,
                padding: "0.4rem 0.6rem",
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "baseline", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "0.45rem", flexWrap: "wrap" }}>
                    <div style={{ fontFamily: "monospace", fontWeight: 700, color: "#f0f2f5", fontSize: "1.05rem" }}>
                      {p.product_id}
                    </div>
                    {isAssemblyPrimary && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          color: "#0e1014",
                          background: "#7ede9e",
                          padding: "0.05rem 0.4rem",
                          borderRadius: 3,
                        }}
                        title="This part is the primary of an assembly. Components on this pallet travel with it through the next station."
                      >
                        ASSEMBLY · PRIMARY
                      </span>
                    )}
                    {isAssemblyComponent && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          color: "#0e1014",
                          background: "#bcd4ff",
                          padding: "0.05rem 0.4rem",
                          borderRadius: 3,
                        }}
                        title={`Travels with assembly primary ${p.assembly_primary_product_id || ""} through this pallet's destination.`}
                      >
                        ↪ with {p.assembly_primary_product_id || "primary"}
                      </span>
                    )}
                  </div>
                  <div style={{ fontFamily: "monospace", fontWeight: 700, color: "#f0f2f5", fontSize: "1rem" }}>
                    {p.qty}
                  </div>
                </div>
                {p.description && (
                  <div style={{ color: "#bcd4ff", fontSize: "0.9rem", lineHeight: 1.2 }}>{p.description}</div>
                )}
                <div style={{ display: "flex", gap: "0.6rem", color: "#9aa4b4", fontSize: "0.82rem" }}>
                  {dims && <span>{dims}</span>}
                  <span>{summarizeStacks(p.stacks)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {overflow && (
        <div style={{ fontSize: "0.85rem", color: "#ffb74d", fontStyle: "italic" }}>
          Combined footprint exceeds 40×48 — physically stack mixed parts vertically (typical for assembly kits).
        </div>
      )}
    </div>
  );
}

function RecommendedToteCard({ tote }) {
  return (
    <div
      style={{
        background: "#1a1d24",
        border: "2px solid #4ec9ff",
        borderRadius: 10,
        padding: "0.6rem 0.9rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#4ec9ff", fontFamily: "monospace" }}>
          {tote.label}
        </div>
        <div style={{ fontSize: "1rem", color: "#9aa4b4" }}>
          {tote.qty} pcs
        </div>
      </div>
      <div style={{ fontFamily: "monospace", fontWeight: 700, color: "#f0f2f5" }}>
        {tote.product_id}
      </div>
      {tote.description && (
        <div style={{ color: "#bcd4ff", fontSize: "0.95rem" }}>{tote.description}</div>
      )}
    </div>
  );
}

// ─── Ready view: top strip + tabs + selected part view ────────────────
// Body shown when there's an active task but no committed packaging plan
// (states: plan_missing, no_sales_order, plan_draft-with-no-parts).
// Sits where the part sidebar + destinations grid would be — leaves
// the JobInfoStrip + UP NEXT card + camera tile fully visible.
function NoPlanBody({ task, state }) {
  const isInternal = state === "no_sales_order";
  const title = isInternal
    ? "Internal job — no packaging plan needed"
    : "No packaging plan committed yet";
  const sub = isInternal
    ? "No outbound routing for this job. Stage parts on the laser cart per shop SOP."
    : `Notify the office — they need to commit a packaging plan for ${task.order_number || `SO-${task.sales_order_id}`} before this job finishes.`;
  return (
    <div style={{
      flex: 1,
      minHeight: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "2rem",
      background: "#14171d",
      borderRadius: 12,
      border: "1px solid #2a2f3a",
    }}>
      <div style={{ textAlign: "center", maxWidth: 720 }}>
        <div style={{ fontSize: "2.4rem", fontWeight: 700, color: isInternal ? "#bcd4ff" : "#ffd76b", marginBottom: "0.8rem" }}>
          {title}
        </div>
        <div style={{ fontSize: "1.3rem", color: "#9aa4b4", lineHeight: 1.4 }}>
          {sub}
        </div>
      </div>
    </div>
  );
}

// ─── Plan-level ship progress strip ───────────────────────────────────
// Mirrors SOPackagingShipProgress on the SO card. Surfaces counts of
// pallets in each status + how many boxes are packed across the SO so
// the operator can see at a glance how close the plan is to closing
// out. Especially useful at end of a multi-job SO — operator can
// confirm "we're 14/16 boxes packed, this cut closes it out" without
// flipping to the desktop.
function KioskShipProgress({ plan }) {
  if (!plan || !plan.pallets || plan.pallets.length === 0) return null;
  const isPickup = plan.fulfillment_type === "pickup";
  const pallets = plan.pallets;
  const building     = pallets.filter(p => (p.status || "Building") === "Building").length;
  const ready        = pallets.filter(p => p.status === "Ready").length;
  const staged       = pallets.filter(p => p.status === "Staged").length;
  const shipped      = pallets.filter(p => p.status === "Shipped").length;
  const consolidated = pallets.filter(p => p.status === "Consolidated").length;
  const nShipping = pallets.length - consolidated;
  // Cartons and stacks: a nested box is packed and sealed with its carton.
  const allBoxes = pallets.flatMap(p => p.boxes || []).filter(b => !isNested(b));
  const nBoxesTotal = allBoxes.length;
  const nBoxesDone  = allBoxes.filter(b => b.status === "Full" || b.status === "Sealed").length;
  const planComplete = plan.status === "Complete";

  const chip = (color, n, label) => (
    <span style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
      <span style={{
        background: color,
        color: "#0f1115",
        fontWeight: 700,
        padding: "0.1rem 0.5rem",
        borderRadius: 5,
        fontFamily: "monospace",
        fontSize: "0.95rem",
        minWidth: 24,
        textAlign: "center",
      }}>{n}</span>
      <span style={{ color: "#bcd4ff" }}>{label}</span>
    </span>
  );

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "0.85rem",
        padding: "0.5rem 0.9rem",
        background: "#1c1f26",
        borderRadius: 8,
        border: "1px solid #2a2f3a",
        fontSize: "0.95rem",
        flexShrink: 0,
      }}
    >
      <span style={{ fontWeight: 700, color: "#e0e4ea", letterSpacing: 0.5 }}>
        📦 {isPickup ? "PICKUP PROGRESS" : "SHIPPING PROGRESS"}
      </span>
      {chip("#6c757d", building, "building")}
      {chip("#ffc107", ready, isPickup ? "ready to stage" : "ready to ship")}
      {staged > 0 && chip("#0dcaf0", staged, "awaiting pickup")}
      {chip("#28a745", shipped, isPickup ? "picked up" : "shipped")}
      <span style={{ color: "#8fa3b8" }}>· {nShipping} total pallet{nShipping === 1 ? "" : "s"}</span>
      {nBoxesTotal > 0 && (
        <span style={{ color: "#8fa3b8", marginLeft: "auto" }}>
          {nBoxesDone}/{nBoxesTotal} {nBoxesTotal === 1 ? "box" : "boxes"} packed
        </span>
      )}
      {planComplete && (
        <span style={{
          background: "#198754",
          color: "white",
          fontWeight: 700,
          padding: "0.15rem 0.6rem",
          borderRadius: 5,
          fontSize: "0.9rem",
        }}>✓ PLAN COMPLETE</span>
      )}
    </div>
  );
}

// ─── Plan-level thickness-assumed warning ─────────────────────────────
// Surfaces parts whose thickness is missing in the part record so the
// office sees the data-quality gap on the kiosk too. Mirrors the
// SOPackagingThicknessWarning banner on the SO card. The kiosk
// operator can't fix this, but knowing the plan is sub-optimal beats
// silently packing per a 0.25" pessimistic fallback.
function KioskThicknessWarning({ plan }) {
  if (!plan || !plan.pallets) return null;
  const missing = new Map(); // product_id -> 1
  for (const p of plan.pallets) {
    for (const b of (p.boxes || [])) {
      for (const z of (b.zones || [])) {
        if (z.thickness_assumed && z.product_id) {
          missing.set(z.product_id, 1);
        }
      }
    }
  }
  if (missing.size === 0) return null;
  const sample = Array.from(missing.keys()).slice(0, 4).join(", ");
  const more = missing.size > 4 ? ` and ${missing.size - 4} more` : "";
  return (
    <div
      style={{
        background: "#3a2e0c",
        border: "2px solid #ffd76b",
        color: "#ffd76b",
        padding: "0.5rem 0.9rem",
        borderRadius: 8,
        fontSize: "0.95rem",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontWeight: 700 }}>
        ⚠ {missing.size} part{missing.size === 1 ? "" : "s"} missing thickness
      </span>
      <span style={{ color: "#e0c478" }}>
        — plan stacked at conservative 0.25″ default. Have office update part data + regenerate for tighter pallets.
      </span>
      <span style={{ color: "#9aa4b4", fontFamily: "monospace", fontSize: "0.85rem", marginLeft: "auto" }}>
        {sample}{more}
      </span>
    </div>
  );
}

function KioskBrakeWarning({ rule, task }) {
  // CARDBOARD BETWEEN SHEETS — rendered whenever the focused job has a
  // part whose "keep sheets apart for the Brake" box is ticked (payload's
  // top-level brake_sheet_rule, present in every task-bearing state, plan
  // or no plan; since 2026-11-04 the box decides, orders and internal jobs
  // alike). Brake setup varies sheet-to-sheet, so every sheet goes on one
  // pallet with cardboard between sheets and the sheet's label on the card.
  if (!rule || !rule.active) return null;
  const sheetsCut = task?.sheet_count ?? rule.sheet_count ?? 0;
  const sheetsReq = task?.sheet_required ?? rule.sheet_required;
  const prods = rule.brake_products || [];
  const sample = prods.slice(0, 4).join(", ");
  const more = prods.length > 4 ? ` +${prods.length - 4} more` : "";
  return (
    <div
      style={{
        background: "#4a1216",
        border: "3px solid #ff6b6b",
        color: "#ffd7d7",
        padding: "0.7rem 1rem",
        borderRadius: 8,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.8rem", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 800, fontSize: "1.5rem", letterSpacing: 0.5, color: "#ff8f8f" }}>
          ⚠ BRAKE JOB — CARDBOARD BETWEEN SHEETS
        </span>
        {Number(sheetsReq) > 0 && (
          <span style={{ marginLeft: "auto", fontFamily: "monospace", fontSize: "1.3rem", fontWeight: 700 }}>
            Sheet {sheetsCut || 0}/{sheetsReq} → cardboard on top
          </span>
        )}
      </div>
      <div style={{ fontSize: "1.1rem", color: "#ffc4c4", lineHeight: 1.35 }}>
        Each sheet needs its own brake setup —{" "}
        <strong style={{ color: "#ffe0e0" }}>never mix parts from different sheets.</strong>{" "}
        Stack the sheets in order on the pallet: cover each sheet's parts with cardboard and
        stick that sheet's printed label on the cardboard.
      </div>
      {sample && (
        <div style={{ fontFamily: "monospace", fontSize: "0.9rem", color: "#d99a9a" }}>
          {sample}{more}
        </div>
      )}
    </div>
  );
}

// ─── REMAKE directive banner ──────────────────────────────────────────
// This job cuts replacement parts for another job's open assembly sets.
// Violet (matches the printed REMAKE slip + queue chip). Top-level
// remake_rule is present in every payload state — remake jobs usually
// have no packaging plan, so this must not depend on `plan`.
function KioskRemakeDirective({ rule }) {
  if (!rule || !rule.active) return null;
  const targets = rule.targets || [];
  return (
    <div
      style={{
        background: "#191933",
        border: "3px solid #7b7bff",
        color: "#d8d8ff",
        padding: "0.7rem 1rem",
        borderRadius: 8,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
      }}
    >
      <div style={{ fontWeight: 800, fontSize: "1.5rem", letterSpacing: 0.5, color: "#a3a3ff" }}>
        🔁 REMAKE PARTS — DO NOT PACK
      </div>
      <div style={{ fontSize: "1.1rem", color: "#c4c4f5", lineHeight: 1.35 }}>
        {rule.detail || "Deliver these parts to the join station below — they never go on a shipping pallet."}
      </div>
      {targets.slice(0, 4).map((t, i) => (
        <div key={i} style={{ fontFamily: "monospace", fontSize: "1.15rem", fontWeight: 700, color: "#e6e6ff" }}>
          {t.product_id}: {t.qty} pcs → Job #{t.source_job_num} at {t.station === "Welding" || t.station === "Fabrication" ? "WELDING" : (t.station || "JOIN").toUpperCase()}
          {t.assembly_name ? `  (${t.assembly_name})` : ""}
        </div>
      ))}
      {targets.length > 4 && (
        <div style={{ fontSize: "0.9rem", color: "#9a9ad0" }}>+{targets.length - 4} more targets — see printed REMAKE slip</div>
      )}
    </div>
  );
}

function ReadyView({
  data,           // the LIVE response data (used as base + fallback)
  clock,
  fetchError,
  activePartId,
  setActivePartId,
  planNotice,
  liveData,       // alias for the live response (kept stable for nav cards)
  viewedData,     // overlay payload when operator clicked Previous/Up Next
  viewedLoading,  // true while the viewed-job fetch is in flight
  onViewPrevious,
  onViewNext,
  onViewLive,
  surplusUnracked, // bundles nobody has racked yet (live response), shown on every job
  surplusToMove, // pulls waiting to be moved from the rack to a pallet (live response)
}) {
  // Pick which payload to render. When viewing a non-current job, the
  // operator's focus shifts to that job's plan + parts. The navigation
  // strip (previous/current/next cards) always reflects the LIVE
  // current task's neighbors regardless of focus.
  const liveResponse = liveData || data;
  const focusedResponse = viewedData || liveResponse;
  const isViewingLive = !viewedData;
  const { task, parts, plans_by_so: plansBySo, recommended_packaging: recommendedPackaging } = focusedResponse;
  const showAllParts = activePartId === ALL_PARTS && (parts || []).length > 1;
  const part = (parts || []).find((p) => p.part_id === activePartId) || (parts || [])[0];
  const [enlarged, setEnlarged] = useState(null);
  // Esc goes back to the live job while viewing another one (the Pi has a keyboard).
  // Not while a picture is enlarged or a dialog (a PIN prompt) is open: Esc closes those.
  useEffect(() => {
    if (isViewingLive) return undefined;
    // Capture phase: runs before a dialog's own Esc handler closes (and unmounts) it.
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented || enlarged) return;
      if (e.target && e.target.closest && e.target.closest('.modal, [role="dialog"]')) return;
      if (document.querySelector('.modal.show, [aria-modal="true"]')) return;
      onViewLive();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isViewingLive, enlarged, onViewLive]);
  // Pre-fetch every part-outline SVG referenced across all destination
  // plans. Single fetch per unique URL; cached for the lifetime of this
  // view. Drives the OutlineOverlay rendering inside both PalletMapSVG
  // and BoxInteriorSVG so operators identify destinations by part shape.
  const outlineCache = useOutlinePaths(useMemo(
    () => Object.values(plansBySo || {}),
    [plansBySo],
  ));
  // When viewing a non-current job + the fetch errored, show an inline
  // banner instead of just silently rendering stale data.
  const viewedFetchError = !isViewingLive && focusedResponse.state === "fetch_error";
  const hasParts = (parts || []).length > 0;
  const hasRecommendation =
    !!recommendedPackaging &&
    (((recommendedPackaging.pallets || []).length > 0) ||
     ((recommendedPackaging.totes || []).length > 0));
  // Guard task lookups during a viewed-job fetch error.
  if (!task) {
    return (
      <div style={{ padding: "2rem", color: "#f0f2f5", textAlign: "center" }}>
        <BigBanner
          title={viewedFetchError ? "Couldn't load that job's packaging guide" : "No task data"}
          subtitle={(focusedResponse._error_message)
            || "The job either doesn't have a Laser task on file, or the server returned an empty payload."}
          tone="warn"
        />
        <button
          onClick={onViewLive}
          style={{
            marginTop: "2rem",
            background: "#0d6efd",
            color: "white",
            border: "none",
            padding: "1rem 2rem",
            borderRadius: 12,
            fontSize: "1.4rem",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          ← Back to current Laser job
        </button>
      </div>
    );
  }
  // Internal jobs have no sales order; show INTERNAL chip instead of SO-XXX
  const isInternal = task.sales_order_id == null;

  return (
    <div style={{ padding: "0.9rem 1rem", color: "#f0f2f5", height: "100vh", display: "flex", flexDirection: "column", gap: "0.7rem", boxSizing: "border-box" }}>
      {/* Top strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1.5rem",
          padding: "0.6rem 1rem",
          background: "#1a1d24",
          borderRadius: 10,
          borderLeft:
            task.is_post_complete ? "6px solid #6c757d"
            : task.task_status === "In Progress" ? "6px solid #28a745"
            : "6px solid #d39e00",
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: "1.25rem", color: "#9aa4b4", fontWeight: 600 }}>LASER</div>
        <div style={{ fontSize: "2.1rem", fontWeight: 700, fontFamily: "monospace" }}>
          Job {task.master_job_num || `#${task.job_id}`}
        </div>
        {isInternal ? (
          <div style={{ fontSize: "1.4rem", fontWeight: 700, color: "#9aa4b4", fontFamily: "monospace", padding: "0.2rem 0.7rem", border: "2px solid #4a5260", borderRadius: 6 }}>
            INTERNAL
          </div>
        ) : (
          <div style={{ fontSize: "2.1rem", fontWeight: 700, color: "#4ec9ff", fontFamily: "monospace" }}>
            {task.order_number || `SO-${task.sales_order_id}`}
          </div>
        )}
        {task.customer_name && (
          <div style={{ fontSize: "1.2rem", color: "#bcd4ff" }}>{task.customer_name}</div>
        )}
        {task.customer_po && (
          <div style={{ fontSize: "1.05rem", color: "#8fa3b8" }}>PO {task.customer_po}</div>
        )}
        {task.job_description && (
          <div style={{ fontSize: "1.05rem", color: "#8fa3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "30vw" }}>
            {task.job_description}
          </div>
        )}
        <SignalLightBar />
        <div
          style={{
            marginLeft: "auto",
            fontSize: "1.1rem",
            padding: "0.3rem 0.9rem",
            background:
              task.is_post_complete ? "#495057"
              : task.task_status === "In Progress" ? "#28a745"
              : task.task_status === "Paused" ? "#d39e00"
              : "#6c757d",
            color: "white",
            borderRadius: 8,
            fontWeight: 700,
          }}
        >
          {task.is_post_complete ? "COMPLETED — PACKING" : task.task_status.toUpperCase()}
        </div>
        <div style={{ fontSize: "1.15rem", color: "#9aa4b4", fontFamily: "monospace" }}>
          {clock}
        </div>
      </div>

      {fetchError && (
        <div style={{ background: "#5a1d1d", color: "#ffd9d9", padding: "0.5rem 0.85rem", borderRadius: 6, fontSize: "1rem", flexShrink: 0 }}>
          ⚠ Live update paused — can't reach server. Showing last-known data.
        </div>
      )}

      <SurplusRackStrip unracked={surplusUnracked} toMove={surplusToMove} />
      {isViewingLive && <PalletScanStrip parts={liveResponse.parts} />}

      {/* Post-complete notice — kiosk keeps showing the just-finished
          job until a new one starts, so the operator finishes packing
          with full routing context on screen. */}
      {task.is_post_complete && (
        <div style={{
          background: "#1c2a3a",
          border: "2px solid #3a5a7a",
          color: "#bcd4ff",
          padding: "0.7rem 1rem",
          borderRadius: 8,
          fontSize: "1.15rem",
          fontWeight: 600,
          flexShrink: 0,
        }}>
          ✓ Job complete — pack remaining items. This view persists until a new laser job starts.
        </div>
      )}

      {/* Optional plan-state notice (Draft, Missing, Internal) — top of body */}
      {planNotice && (
        <div style={{
          background: planNotice.tone === "warn" ? "#5a4a16" : "#1c2a3a",
          border: `2px solid ${planNotice.tone === "warn" ? "#a8882b" : "#3a5a7a"}`,
          color: planNotice.tone === "warn" ? "#ffd76b" : "#bcd4ff",
          padding: "0.7rem 1rem",
          borderRadius: 8,
          fontSize: "1.15rem",
          fontWeight: 600,
          flexShrink: 0,
        }}>
          {planNotice.text}
        </div>
      )}

      {/* Time / sheet counter / cut sheet & material info — 3-card row
          with Previous Job + Current + Up Next navigation. Clicking
          Prev / Up Next swaps the focused job's packaging guide below. */}
      <JobInfoStrip
        task={task}
        liveTask={liveResponse?.task}
        isViewingLive={isViewingLive}
        onViewPrevious={onViewPrevious}
        onViewNext={onViewNext}
        onViewLive={onViewLive}
      />

      {/* Off-current bar: the operator went to Previous Job / Up Next. Big and
          impossible to miss, with a big way back (and Esc). */}
      {!isViewingLive && (
        <div
          data-testid="viewing-other-job"
          style={{
            background: "#4a3608",
            border: "3px solid #ffb020",
            color: "#ffe2a6",
            padding: "0.5rem 0.9rem",
            borderRadius: 10,
            fontSize: "1.3rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            flexShrink: 0,
          }}
        >
          <span>
            👁 You are looking at <strong>Job {task.master_job_num || `#${task.job_id}`}</strong>
            {viewedLoading && " (loading…)"}, NOT the job on the laser
            {liveResponse?.task?.master_job_num ? ` (Job ${liveResponse.task.master_job_num})` : ""}.
          </span>
          <button
            type="button"
            data-testid="back-to-live"
            onClick={onViewLive}
            style={{
              marginLeft: "auto",
              background: "#0d6efd",
              color: "white",
              border: "3px solid #8ec5ff",
              padding: "0.6rem 1.6rem",
              borderRadius: 10,
              fontWeight: 800,
              cursor: "pointer",
              fontSize: "1.4rem",
              whiteSpace: "nowrap",
            }}
          >
            ◀ BACK TO LIVE JOB <span style={{ fontSize: "0.95rem", opacity: 0.85 }}>(Esc)</span>
          </button>
        </div>
      )}

      {/* Plan-level summary strips — ship progress + data-quality
          warnings. Only render when we actually have a plan (skips
          internal jobs and plan-missing/draft states). */}
      {focusedResponse.remake_rule && (
        <KioskRemakeDirective rule={focusedResponse.remake_rule} />
      )}
      {focusedResponse.brake_sheet_rule && (
        <KioskBrakeWarning rule={focusedResponse.brake_sheet_rule} task={task} />
      )}
      {focusedResponse.plan && <KioskShipProgress plan={focusedResponse.plan} />}
      {focusedResponse.plan && <KioskThicknessWarning plan={focusedResponse.plan} />}

      {/* Body: the part views on the left, the rail (camera + one more panel)
          on the right. Side by side, so the camera never covers a card. */}
      <div style={{ display: "flex", gap: "0.8rem", flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", gap: "0.7rem" }}>
          {hasParts ? (
            <>
              {/* All parts + per-part tabs (hidden for single-part jobs) */}
              <PartTabs parts={parts} activePartId={showAllParts ? ALL_PARTS : part?.part_id} onSelect={setActivePartId} />

              {showAllParts ? (
                <AllPartsUnload parts={parts} outlineCache={outlineCache} onOpenPart={setActivePartId} />
              ) : (
                <PartView part={part} source={task} plansBySo={plansBySo} outlineCache={outlineCache} onEnlarge={setEnlarged} />
              )}
            </>
          ) : hasRecommendation ? (
            <RecommendedPackagingBody recommendation={recommendedPackaging} task={task} />
          ) : (
            <NoPlanBody task={task} state={focusedResponse.state} />
          )}
        </div>
        <KioskRail>
          {hasParts && task?.cut_sheet_id ? <SheetMapPanel task={task} parts={parts} /> : null}
        </KioskRail>
      </div>

      {/* Click-to-enlarge modal */}
      {enlarged && (
        <EnlargeModal title={enlarged.title} onClose={() => setEnlarged(null)}>
          {enlarged.kind === "pallet" && (
            <PalletMapSVG
              plan={enlarged.plan}
              highlightPalletId={enlarged.highlightPalletId}
              highlightBoxId={enlarged.highlightBoxId}
              outlineCache={outlineCache}
              scalePxPerIn={computePalletEnlargedScale(enlarged.plan)}
              showLabels
            />
          )}
          {enlarged.kind === "box" && (
            <BoxInteriorSVG
              box={enlarged.box}
              highlightPartId={enlarged.highlightPartId}
              outlineCache={outlineCache}
              scalePxPerIn={computeBoxEnlargedScale(enlarged.box)}
              showLabels
            />
          )}
          {enlarged.kind === "image" && enlarged.url && (
            <img
              src={enlarged.url}
              alt={enlarged.title || "part"}
              style={{
                maxWidth: "85vw",
                maxHeight: "82vh",
                objectFit: "contain",
                background: "#fafafa",
                borderRadius: 10,
              }}
            />
          )}
        </EnlargeModal>
      )}
    </div>
  );
}

export default function LaserCurrentPackagingPage() {
  useKioskBody();
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [clock, setClock] = useState(fmtClock(new Date()));
  const [activePartId, setActivePartIdRaw] = useState(null);
  // Track latest activePartId in a ref so the polling closure (which
  // has a stable [] dep array) sees current state instead of the stale
  // initial-render null. Without this, every 5s poll re-asserted "first
  // part" and reverted the operator's tab click within seconds.
  const activePartIdRef = useRef(null);
  useEffect(() => { activePartIdRef.current = activePartId; }, [activePartId]);
  // Cache the last "ready" payload so a transient idle blip doesn't wipe
  // the display between jobs on the laser.
  const lastReady = useRef(null);
  // Track which job we're showing so active-part selection resets when
  // the job changes.
  const lastJobId = useRef(null);

  // ── Cross-job navigation (May 2026) ────────────────────────────────
  // Operator can click Previous Job / Up Next to view that job's
  // packaging guide. While viewing a non-current job, the live polling
  // of /laser/current-packaging keeps running in the background (so
  // the navigation cards stay fresh) — viewedData is a one-shot fetch
  // from /laser/packaging-for-job/{job_id} that overlays the center
  // view. Stays put across live-job changes — operator might still be
  // packing the viewed job when the laser starts a new one. They go
  // back to live via the "Back to current" button.
  const [viewedJobId, setViewedJobId] = useState(null);
  const [viewedData, setViewedData] = useState(null);
  const [viewedLoading, setViewedLoading] = useState(false);
  const viewedJobIdRef = useRef(null);
  useEffect(() => { viewedJobIdRef.current = viewedJobId; }, [viewedJobId]);

  // Wrap setter so callers don't have to remember the name.
  const setActivePartId = (id) => setActivePartIdRaw(id);

  const fetchNow = useCallback(async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/laser/current-packaging`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      setFetchError(false);
      setData(payload);
      if (payload.state === "ready") {
        payload._stamp = Date.now();
        lastReady.current = payload;
      }
      // Part selection for every state that shows a job (a Draft plan's job has parts too).
      if (payload.task) {
        const newJobId = payload.task?.job_id;
        const isViewingNonLive = viewedJobIdRef.current != null;
        if (newJobId !== lastJobId.current) {
          lastJobId.current = newJobId;
          // Only reset the active-part selection when viewing live —
          // if the operator is focused on a different job (Previous /
          // Up Next), their part selection is for THAT job's part
          // list, not the new live job's.
          if (!isViewingNonLive) {
            setActivePartIdRaw(defaultPartSelection(payload.parts));
          }
        } else if (!isViewingNonLive
                   && activePartIdRef.current == null
                   && (payload.parts || []).length) {
          // Same job, but no part selected (e.g., parts arrived after
          // initial idle render). Default to all parts (or the only one).
          setActivePartIdRaw(defaultPartSelection(payload.parts));
        }
        // Otherwise: same job + operator has a part selected → leave it
        // alone. Polling no longer revert their tab click.
      }
    } catch (err) {
      setFetchError(true);
      console.warn("laser-kiosk fetch failed:", err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch a specific job's packaging guide for the navigation overlay.
  // One-shot; no polling. The live current-packaging poll continues in
  // the background so the prev/next navigation cards stay current.
  const fetchViewedJob = useCallback(async (jobId) => {
    if (!jobId) return;
    setViewedLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/laser/packaging-for-job/${jobId}`,
        { cache: "no-store" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      // The operator went back to live (or to another job) while this loaded: drop it.
      if (viewedJobIdRef.current !== jobId) return;
      // Reset active-part selection when switching jobs so the first
      // part of the viewed job is selected.
      setViewedData(payload);
      setActivePartIdRaw(defaultPartSelection(payload.parts));
    } catch (err) {
      console.warn("laser-kiosk viewed-job fetch failed:", err);
      if (viewedJobIdRef.current !== jobId) return;
      // Surface failure as a banner — keep viewedJobId set so the user
      // sees the "couldn't load" state rather than silently snapping
      // back to live.
      setViewedData({
        state: "fetch_error",
        task: null, plan: null, parts: [],
        requested_job_id: jobId,
        _error_message: err && err.message ? err.message : String(err),
      });
    } finally {
      if (viewedJobIdRef.current === jobId) setViewedLoading(false);
    }
  }, []);

  const handleViewPrevious = useCallback(() => {
    const prev = data?.task?.previous_task;
    if (!prev?.job_id) return;
    viewedJobIdRef.current = prev.job_id;
    setViewedJobId(prev.job_id);
    fetchViewedJob(prev.job_id);
  }, [data, fetchViewedJob]);

  const handleViewNext = useCallback(() => {
    const next = data?.task?.next_task;
    if (!next?.job_id) return;
    viewedJobIdRef.current = next.job_id;
    setViewedJobId(next.job_id);
    fetchViewedJob(next.job_id);
  }, [data, fetchViewedJob]);

  const handleViewLive = useCallback(() => {
    viewedJobIdRef.current = null;
    setViewedJobId(null);
    setViewedData(null);
    // Reset part selection to the current job's default (all parts, or the only one).
    setActivePartIdRaw(defaultPartSelection(data?.parts));
  }, [data]);

  // A pallet scan is judged against the live job, so its tick is on the live job's tiles.
  const handleScanBackToLive = useCallback(() => {
    if (viewedJobIdRef.current != null) handleViewLive();
  }, [handleViewLive]);

  useEffect(() => {
    fetchNow();
    const id = setInterval(fetchNow, 5000);
    return () => clearInterval(id);
  }, [fetchNow]);

  useEffect(() => {
    const id = setInterval(() => setClock(fmtClock(new Date())), 30000);
    return () => clearInterval(id);
  }, []);

  if (!data) {
    // Haven't gotten a response yet. If we've been trying and failing,
    // show a helpful message instead of a permanent "Loading…".
    if (fetchError) {
      return (
        <BigBanner
          title="Can't reach server"
          subtitle="Retrying every 5s. Check that dev.jobqworks.com is up."
          tone="warn"
        />
      );
    }
    return <BigBanner title="Loading…" tone="idle" />;
  }

  // Common props for ReadyView — bundles the navigation state so we
  // don't have to thread 8 props through every case branch.
  const readyViewProps = {
    clock,
    fetchError,
    activePartId,
    setActivePartId,
    liveData: data,             // always the LIVE response
    viewedData,                 // null when viewing live; payload otherwise
    viewedLoading,
    onViewPrevious: handleViewPrevious,
    onViewNext: handleViewNext,
    onViewLive: handleViewLive,
    surplusUnracked: data.surplus_unracked,
    surplusToMove: data.surplus_to_move,     // rack pieces promised to a pallet, not moved yet
  };

  return (
    <KioskScanContext.Provider value={{ scanOn: !!data.laser_scan_on, viewingLive: viewedJobId == null }}>
      <SurplusRefreshContext.Provider value={fetchNow}>
        <SurplusCratesContext.Provider value={!!data.surplus_crates_on}>
          <KioskScanner on={!!data.laser_scan_on} onDone={fetchNow} onPalletOk={handleScanBackToLive} />
          {renderState()}
        </SurplusCratesContext.Provider>
      </SurplusRefreshContext.Provider>
    </KioskScanContext.Provider>
  );

  function renderState() {
  switch (data.state) {
    case "ready":
      return <ReadyView data={data} {...readyViewProps} />;

    case "plan_draft":
      return (
        <ReadyView
          data={data}
          {...readyViewProps}
          planNotice={{
            tone: "warn",
            text: `⚠ Plan is in DRAFT for ${data.task?.order_number || `SO-${data.task?.sales_order_id}`} — not yet committed. Notify the office.`,
          }}
        />
      );

    case "plan_missing":
      return (
        <ReadyView
          data={data}
          {...readyViewProps}
          planNotice={{
            tone: "warn",
            text: `⚠ No packaging plan committed for ${data.task?.order_number || `SO-${data.task?.sales_order_id}`} yet. Notify the office.`,
          }}
        />
      );

    case "no_sales_order":
      return (
        <ReadyView
          data={data}
          {...readyViewProps}
          planNotice={{
            tone: "info",
            text: `ℹ Internal job — no outbound packaging routing.`,
          }}
        />
      );

    case "internal_recommended":
      return (
        <ReadyView
          data={data}
          {...readyViewProps}
          planNotice={{
            tone: "info",
            text: `ℹ Internal job — recommended packaging shown below (advisory).`,
          }}
        />
      );

    case "idle":
    default:
      if (lastReady.current && Date.now() - (lastReady.current._stamp || 0) < 60_000) {
        return (
          <ReadyView
            data={lastReady.current}
            {...readyViewProps}
            liveData={lastReady.current}
          />
        );
      }
      return (
        <>
          <BigBanner title="Laser is idle" subtitle={clock} tone="idle" />
          {(data.surplus_unracked?.bundles?.length > 0 || data.surplus_to_move?.pulls?.length > 0) && (
            <div style={{ position: "fixed", top: "1rem", left: "1rem", right: "1rem", zIndex: 10 }}>
              <SurplusRackStrip unracked={data.surplus_unracked} toMove={data.surplus_to_move} maxRows={6} />
            </div>
          )}
        </>
      );
  }
  }
}
